import { useState, useEffect, useMemo } from "react";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import {
  collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc,
  query, where, orderBy, serverTimestamp, writeBatch,
} from "firebase/firestore";
import waterTankerIcon from "../assets/water-tanker-icon.png";

// ════════════════════════════════════════════════════════════════
// WaterScreen.jsx — Adyantam Building Circle
//
// Single file for all Water functionality:
//   • Meter-based billing (existing — used by BSKR)
//   • Equal-Split billing (new — for societies without meters)
//   • Tanker delivery logging
//   • Tanker ordering, payments, and batch payments to supplier
//
// Retired files (kept only as thin re-export shims so nothing else
// in the app needs to change):
//   • WaterMeterReadingsScreen.jsx  → re-exports WaterScreen as default
//
// TankerOrderForm.jsx (old Tanker Entry Log screen + tankerEntries
// collection) has been fully removed as of 7 July 2026 — it was not
// reachable from any menu/tab in the live app. Delete TankerOrderForm.jsx
// from the project; nothing imports it anymore.
//
// PassbookScreen.jsx now imports { WaterTankerExpensesTab } from here
// for its "🚛 Water Tanker" toggle view.
// ════════════════════════════════════════════════════════════════

// ── Constants ──────────────────────────────────────────────────
// Fallback only — used if the "flats" collection can't be read, or a
// flat is missing its own sortOrder field. The real, live per-society
// flat list + order comes from Firestore (see loadFlatOrder below).
const DEFAULT_FLAT_ORDER = ["G1","G2","G3","101","102","103","201","202","203","301","302","303"];

// Fetches the real flat list from Firestore's "flats" collection and
// sorts it by each flat's own "sortOrder" number. Falls back to the
// fixed BSKR list above if the fetch fails or sortOrder is missing,
// so this never breaks the screen even mid-rollout.
async function loadFlatOrder() {
  try {
    const snap = await getDocs(collection(db, "flats"));
    const flats = snap.docs.map(d => ({ flatNo: d.id, ...d.data() }));
    const allHaveSortOrder = flats.length > 0 && flats.every(f => typeof f.sortOrder === "number");
    if (!allHaveSortOrder) return DEFAULT_FLAT_ORDER;
    return flats.sort((a, b) => a.sortOrder - b.sortOrder).map(f => f.flatNo);
  } catch (e) {
    console.error("Could not load flat order, using fallback list:", e);
    return DEFAULT_FLAT_ORDER;
  }
}

function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

// True only on the 1st calendar day of the month (real device date, all day 00:00–23:59)
function isFirstOfMonth() {
  return new Date().getDate() === 1;
}

// "August 2026" style label for the month AFTER today's real calendar month
function nextMonthLabel() {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

// Returns today's date as "YYYY-MM-DD" (used to pre-fill date fields)
function todayISO() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function prevMonthKey(monthKey) {
  const [y, m] = monthKey.split("-").map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmt(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN");
}

// ── Rollover-safe liters calculation ──────────────────────────
// Meter has 8 total digits (5 black + 3 red), so it counts up to 99999999
// before rolling back to 0. Normally current >= previous and it's a simple
// subtraction. If current < previous, the meter has rolled over during the
// month — so we count what was left before the roll (up to the max, plus
// the step back to zero) plus what's been counted since the roll.
const METER_MAX_DIGITS = 99999999; // 8 digits, all 9s
function calcLitersUsed(prev, curr) {
  const p = Number(prev) || 0;
  const c = Number(curr) || 0;
  if (c >= p) {
    return c - p; // normal case, no rollover
  }
  // Rollover case: meter passed its max and wrapped back to 0
  const leftoverBeforeReset = (METER_MAX_DIGITS - p) + 1;
  return leftoverBeforeReset + c;
}

// ── Financial year helpers (Apr → current month, YTD) for Reading History ──
function getFYMonths(baseDate = new Date()) {
  const y = baseDate.getFullYear();
  const m = baseDate.getMonth() + 1; // 1-12
  const fyStartYear = m >= 4 ? y : y - 1; // FY starts in April
  // Only months from April up to and including the current month — no future/empty months
  const monthsElapsed = m >= 4 ? (m - 4 + 1) : (m + 12 - 4 + 1);
  const months = [];
  for (let i = 0; i < monthsElapsed; i++) {
    const d = new Date(fyStartYear, 3 + i, 1); // month index 3 = April
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return months;
}

// Returns the current financial year as a label like "2026-27".
// Recalculates every time it's called — never frozen to one year.
function currentFYLabel() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1; // 1-12
  const fyStartYear = m >= 4 ? y : y - 1;
  return `${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`;
}

function monthPillLabel(monthKey) {
  const [y, m] = monthKey.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return `${d.toLocaleString("en-US", { month: "short" })}-${String(y).slice(-2)}`;
}

function fullMonthYearLabel(monthKey) {
  const [y, m] = monthKey.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

function pad8(n) {
  return n === null || n === undefined || n === "" ? "—" : String(n).padStart(8, "0");
}

// ── Sub-components ─────────────────────────────────────────────
function ReadingRow({ flat, reading, prevReading, onChange, canEdit, saved }) {
  const liters = reading !== "" && prevReading !== ""
    ? calcLitersUsed(prevReading, reading)
    : null;

  return (
    <div style={{
      background: "#1a1a24", border: `1px solid ${saved ? "#4caf5040" : "#2e2e40"}`,
      borderRadius: "12px", padding: "12px 14px", marginBottom: "8px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        {/* Flat label */}
        <div style={{
          width: "44px", height: "44px", borderRadius: "10px",
          background: "#6c63ff18", border: "1px solid #6c63ff40",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "13px", fontWeight: "700", color: "#6c63ff", flexShrink: 0,
        }}>
          {flat}
        </div>

        {/* Prev reading */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "10px", color: "#555570", marginBottom: "3px" }}>Prev</div>
          <div style={{ fontSize: "13px", color: "#8888a8" }}>
            {prevReading !== "" ? pad8(prevReading) : <span style={{ color: "#3a3a50" }}>—</span>}
          </div>
        </div>

        {/* Current reading input / display */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "10px", color: "#555570", marginBottom: "3px" }}>Current</div>
          {canEdit ? (
            <input
              type="number"
              value={reading}
              placeholder="Enter"
              onChange={e => onChange(flat, e.target.value)}
              style={{
                width: "100%", background: "#0f0f13",
                border: `1px solid ${reading !== "" ? "#6c63ff60" : "#2e2e40"}`,
                borderRadius: "8px", padding: "6px 10px",
                color: "#e8e8f0", fontSize: "13px",
                outline: "none", boxSizing: "border-box",
              }}
            />
          ) : (
            <div style={{ fontSize: "13px", color: "#e8e8f0" }}>
              {reading !== "" ? pad8(reading) : <span style={{ color: "#3a3a50" }}>—</span>}
            </div>
          )}
        </div>

        {/* Usage */}
        <div style={{ width: "60px", textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: "10px", color: "#555570", marginBottom: "3px" }}>Used</div>
          <div style={{
            fontSize: "13px", fontWeight: "600",
            color: liters !== null ? "#42a5f5" : "#3a3a50",
          }}>
            {liters !== null ? `${liters}L` : "—"}
          </div>
        </div>
      </div>

      {/* Saved indicator */}
      {saved && (
        <div style={{ fontSize: "10px", color: "#4caf50", marginTop: "6px" }}>✅ Saved</div>
      )}
    </div>
  );
}

// ── Bill Review Row (MC review/modify before lock) ───────────────
function BillReviewRow({ flat, litersUsed, calculatedBill, editedBill, onChange, locked }) {
  const isEdited = editedBill !== "" && Number(editedBill) !== calculatedBill;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "10px",
      background: "#1a1a24", border: `1px solid ${isEdited ? "#ffa72640" : "#2e2e40"}`,
      borderRadius: "10px", padding: "10px 12px", marginBottom: "6px",
    }}>
      <div style={{
        width: "40px", height: "40px", borderRadius: "8px",
        background: "#6c63ff18", border: "1px solid #6c63ff40",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "12px", fontWeight: "700", color: "#6c63ff", flexShrink: 0,
      }}>
        {flat}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "10px", color: "#555570", marginBottom: "2px" }}>Liters Used</div>
        <div style={{ fontSize: "13px", color: "#42a5f5", fontWeight: "600" }}>{litersUsed}L</div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "10px", color: "#555570", marginBottom: "2px" }}>Calculated</div>
        <div style={{ fontSize: "13px", color: "#8888a8" }}>{fmt(calculatedBill)}</div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "10px", color: "#555570", marginBottom: "2px" }}>
          Final Bill {isEdited && <span style={{ color: "#ffa726" }}>(edited)</span>}
        </div>
        {locked ? (
          <div style={{ fontSize: "13px", color: "#e8e8f0", fontWeight: "600" }}>
            {fmt(editedBill !== "" ? editedBill : calculatedBill)}
          </div>
        ) : (
          <input
            type="number"
            value={editedBill}
            placeholder={String(calculatedBill)}
            onChange={e => onChange(flat, e.target.value)}
            style={{
              width: "100%", background: "#0f0f13",
              border: `1px solid ${isEdited ? "#ffa72660" : "#2e2e40"}`,
              borderRadius: "8px", padding: "6px 10px",
              color: "#e8e8f0", fontSize: "13px",
              outline: "none", boxSizing: "border-box",
            }}
          />
        )}
      </div>
    </div>
  );
}

// ── Reading History (per-flat, current FY, fetched on tap) ──────
function WaterHistorySection({ flatId }) {
  const fyMonths = useMemo(() => getFYMonths(), []);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthKey());
  const [historyData,   setHistoryData]   = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!flatId) return;
    let cancelled = false;
    async function fetchMonth() {
      setHistoryLoading(true);
      try {
        const [readingSnap, billSnap] = await Promise.all([
          getDoc(doc(db, "meterReadings", `${flatId}_${selectedMonth}`)),
          getDoc(doc(db, "waterBills",    `${flatId}_${selectedMonth}`)),
        ]);
        if (cancelled) return;
        const reading = readingSnap.exists() ? readingSnap.data() : null;
        const bill    = billSnap.exists()    ? billSnap.data()    : null;
        setHistoryData({
          prevReading:    reading?.previousReading ?? null,
          currentReading: reading?.reading ?? null,
          litersUsed:     reading?.litersUsed ?? bill?.litersUsed ?? null,
          tankerCost:     bill?.tankerCost ?? null,
          billAmount:     bill?.billAmount ?? null,
        });
      } catch (e) {
        if (!cancelled) setHistoryData(null);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    }
    fetchMonth();
    return () => { cancelled = true; };
  }, [flatId, selectedMonth]);

  if (!flatId) return null;

  const columns = [
    { label: "Prev reading",    value: pad8(historyData?.prevReading) },
    { label: "Current reading", value: pad8(historyData?.currentReading) },
    {
      label: "Liters used",
      value: historyData?.litersUsed != null ? `${historyData.litersUsed}L` : "—",
      subValue: historyData?.litersUsed != null ? `${(historyData.litersUsed / 1000).toFixed(3)} KL` : null,
    },
    { label: "Tanker cost",     value: historyData?.tankerCost != null ? fmt(historyData.tankerCost) : "—" },
    { label: "Bill amount",     value: historyData?.billAmount != null ? fmt(historyData.billAmount) : "—" },
  ];

  return (
    <div style={{ marginTop: "24px" }}>
      <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0", marginBottom: "10px" }}>
        📊 Water meter reading history — Apr to current month ({currentFYLabel()})
      </div>

      {/* Month pills — current FY, scrolls sideways */}
      <div style={{
        display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "8px", marginBottom: "12px",
      }}>
        {fyMonths.map(mKey => {
          const isSelected = mKey === selectedMonth;
          return (
            <button
              key={mKey}
              onClick={() => setSelectedMonth(mKey)}
              style={{
                flexShrink: 0, padding: "8px 14px", borderRadius: "20px",
                border: `1px solid ${isSelected ? "#6c63ff" : "#2e2e40"}`,
                background: isSelected ? "#6c63ff20" : "#1a1a24",
                color: isSelected ? "#6c63ff" : "#8888a8",
                fontSize: "12px", fontWeight: isSelected ? "700" : "500",
                cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              {monthPillLabel(mKey)}
            </button>
          );
        })}
      </div>

      {/* Individual reading cards — same look as Dashboard's summary cards */}
      {historyLoading ? (
        <div style={{
          background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
          textAlign: "center", color: "#555570", fontSize: "12px", padding: "14px",
        }}>
          Loading…
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
          {columns.map((col, i) => (
            <div key={i} style={{
              background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
              padding: "10px 6px", textAlign: "center", minWidth: 0,
            }}>
              <div style={{ fontSize: "9px", color: "#8888a8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                {col.label}
              </div>
              <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0", wordBreak: "break-word" }}>
                {col.value}
              </div>
              {col.subValue && (
                <div style={{ fontSize: "9px", color: "#42a5f5", marginTop: "2px" }}>
                  {col.subValue}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{
        fontSize: "11px", color: "#42a5f5", textAlign: "center", marginTop: "8px",
        background: "#42a5f518", border: "1px solid #42a5f540", borderRadius: "8px",
        padding: "8px 10px",
      }}>
        💧 Your bill depends on how much water your flat used, out of the total used by all flats. Example: if your flat used 10% of all the water this month, you pay 10% of the total tanker cost.
      </div>
    </div>
  );
}

// ── Usage Trend Chart (per-flat, YTD, line chart of liters used) ──
function WaterUsageTrendChart({ flatId }) {
  const fyMonths = useMemo(() => getFYMonths(), []);
  const [points, setPoints] = useState(null); // [{ month, liters }] — only months with data
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!flatId) return;
    let cancelled = false;
    async function fetchTrend() {
      setLoading(true);
      try {
        const results = await Promise.all(
          fyMonths.map(mKey => getDoc(doc(db, "meterReadings", `${flatId}_${mKey}`)))
        );
        if (cancelled) return;
        const pts = [];
        results.forEach((snap, i) => {
          if (snap.exists()) {
            const data = snap.data();
            if (data.litersUsed != null) {
              pts.push({ month: fyMonths[i], liters: data.litersUsed });
            }
          }
        });
        setPoints(pts);
      } catch (e) {
        if (!cancelled) setPoints([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchTrend();
    return () => { cancelled = true; };
  }, [flatId, fyMonths]);

  if (!flatId) return null;

  return (
    <div style={{ marginTop: "20px" }}>
      <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0", marginBottom: "10px" }}>
        📈 Usage trend — Apr to current month ({currentFYLabel()})
      </div>
      <div style={{
        background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", padding: "10px 12px",
      }}>
        {loading ? (
          <div style={{ textAlign: "center", color: "#555570", fontSize: "12px", padding: "14px" }}>
            Loading…
          </div>
        ) : !points || points.length === 0 ? (
          <div style={{ textAlign: "center", color: "#555570", fontSize: "12px", padding: "14px" }}>
            No readings yet this year.
          </div>
        ) : (
          <UsageLineChart points={points} />
        )}
      </div>
    </div>
  );
}

// Simple SVG line chart — no external chart library needed
function UsageLineChart({ points }) {
  const width = 320;
  const height = 140;
  const padLeft = 14;
  const padRight = 14;
  const padTop = 24; // room for the value label sitting above each dot
  const padBottom = 22; // room for the month label under each dot
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;

  const values = points.map(p => p.liters);
  const maxV = Math.max(...values, 1);
  const minV = 0; // baseline at zero for usage charts

  const stepX = points.length > 1 ? chartW / (points.length - 1) : 0;
  const scaleY = v => padTop + chartH - ((v - minV) / (maxV - minV || 1)) * chartH;

  const coords = points.map((p, i) => ({
    x: points.length > 1 ? padLeft + i * stepX : padLeft + chartW / 2,
    y: scaleY(p.liters),
  }));

  const linePath = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L ${coords[coords.length - 1].x.toFixed(1)} ${(padTop + chartH).toFixed(1)} L ${coords[0].x.toFixed(1)} ${(padTop + chartH).toFixed(1)} Z`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", display: "block" }}>
      {/* Baseline */}
      <line x1={padLeft} y1={padTop + chartH} x2={width - padRight} y2={padTop + chartH} stroke="#2e2e40" strokeWidth="1" />

      {/* Shaded area under the line */}
      <path d={areaPath} fill="#42a5f5" fillOpacity="0.12" stroke="none" />

      {/* Trend line */}
      <path d={linePath} fill="none" stroke="#42a5f5" strokeWidth="2" />

      {/* Points + value label above + month label below */}
      {coords.map((c, i) => (
        <g key={i}>
          <circle cx={c.x} cy={c.y} r="4" fill="#42a5f5" stroke="#1a1a24" strokeWidth="2" />
          <text x={c.x} y={Math.max(c.y - 10, 11)} fontSize="11" fill="#e8e8f0" fontWeight="700" textAnchor="middle">
            {points[i].liters}
          </text>
          <text x={c.x} y={height - 6} fontSize="10" fill="#8888a8" textAnchor="middle">
            {monthPillLabel(points[i].month)}
          </text>
        </g>
      ))}
    </svg>
  );
}

// ── Main Screen ────────────────────────────────────────────────
function MeterBasedWaterView({ canEdit, user, isMC, isPresident }) {
  const { isAdyantam } = useAuth();
  const isAdyantamUser = isAdyantam ? isAdyantam() : false;

  const todayMonth = currentMonthKey();

  const [readings,     setReadings]     = useState({}); // flatId → current reading string
  const [prevReadings, setPrevReadings] = useState({}); // flatId → prev reading string
  const [savedFlats,   setSavedFlats]   = useState(new Set());
  const [saving,       setSaving]       = useState(null); // flatId being saved
  const [saveAll,      setSaveAll]      = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [tankers,      setTankers]      = useState([]);
  const [settings,     setSettings]     = useState(null);
  const [flatOrder,    setFlatOrder]    = useState(DEFAULT_FLAT_ORDER); // real per-society flat list, from Firestore

  // ── Tanker summary widget (Cumulative / This month / Total, by month tab) ──
  // For everyone else, this pill ONLY drives the summary boxes below.
  // For the Adyantam role, this same pill ALSO drives which month's meter
  // readings/bills get loaded, edited, and saved — this is the one-time
  // catch-up ability for April–June 2026 (frozen decision, Adyantam-only).
  const [summaryMonth,       setSummaryMonth]       = useState(todayMonth);
  const [tankerMonthlyStats, setTankerMonthlyStats] = useState({}); // monthKey → { count, cost }
  const fyMonths = useMemo(() => getFYMonths(), []);

  const month     = isAdyantamUser ? summaryMonth : todayMonth;
  const prevMonth = prevMonthKey(month);

  // ── Billing engine state ──────────────────────────────────────
  const [waterBills,    setWaterBills]    = useState({});
  const [editedBills,   setEditedBills]   = useState({});
  const [billsConfirmed, setBillsConfirmed] = useState(false); // step 1: Sec/Treas confirmed
  const [billsLocked,   setBillsLocked]   = useState(false);   // step 2: President locked
  const [confirming,    setConfirming]    = useState(false);
  const [corpus,        setCorpus]        = useState(null);
  const [carryForward,  setCarryForward]  = useState({});

  const canManageBills = canEdit && (isMC ? isMC() : true);
  const approvalFlow   = settings?.waterApprovalFlow || "two-step";
  // Local alias so all the FLAT_ORDER usages below (meter wizard, totals,
  // etc.) automatically use this society's real, live flat list.
  const FLAT_ORDER      = flatOrder;

  // ── Meter entry window ──────────────────────────────────────
  // Normal rule (everyone): strictly the 1st of the real calendar month only.
  // Open all day on the 1st (until midnight), and closes the instant either:
  //   (a) the President approves & locks the bill, or
  //   (b) the day ends and it's no longer the 1st — whichever comes first.
  // If a month is missed, it is NOT auto-reopened later — it stays unbilled.
  //
  // Adyantam-only exception (frozen decision — one-time Apr/Jun 2026 catch-up,
  // not exposed to President/Secretary/Treasurer/Watchman): when Adyantam has
  // picked a past month that has no locked bills yet, the entry window opens
  // regardless of today's real date. A locked month stays frozen for Adyantam
  // too — no exceptions, ever.
  const isPastUnlockedCatchUp = isAdyantamUser && month !== todayMonth && !billsLocked;
  const entryWindowActive = !billsLocked && (isFirstOfMonth() || isPastUnlockedCatchUp);
  const isPresidentUser  = isPresident ? isPresident() : false;
  const isSecretaryUser  = user?.role === "Secretary";
  const isSecTreas       = canManageBills && !isPresidentUser; // Secretary or Treasurer
  const isWatchman       = user?.role === "Watchman";

  // ── Watchman wizard state ──────────────────────────────────────
  const [wizardStep,    setWizardStep]    = useState(0);
  const [wizardInput,   setWizardInput]   = useState("");
  const [wizardStage,   setWizardStage]   = useState("entry"); // "entry" | "recheck"
  const [wizardError,   setWizardError]   = useState("");
  const [wizardSaving,  setWizardSaving]  = useState(false);
  const [wizardDone,    setWizardDone]    = useState(false);

  // Load existing readings for this month + prev month
  useEffect(() => {
    async function load() {
      try {
        // Settings
        const settSnap = await getDoc(doc(db, "settings", "init"));
        let settData = null;
        if (settSnap.exists()) {
          settData = settSnap.data();
          setSettings(settData);
        }

        // Flat list + order (from each flat's own sortOrder field)
        setFlatOrder(await loadFlatOrder());

        // Current month readings
        const currSnap = await getDocs(
          query(collection(db, "meterReadings"), where("month", "==", month))
        );
        const curr = {};
        currSnap.docs.forEach(d => { curr[d.data().flatId] = String(d.data().reading ?? ""); });

        // Prev month readings (for reference)
        const prevSnap = await getDocs(
          query(collection(db, "meterReadings"), where("month", "==", prevMonth))
        );
        const prev = {};
        prevSnap.docs.forEach(d => { prev[d.data().flatId] = String(d.data().reading ?? ""); });

        // Tankers this month
        const tankerSnap = await getDocs(
          query(collection(db, "tankerOrders"), where("month", "==", month))
        );
        const tankerDocs = tankerSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setTankers(tankerDocs);

        // Water bills already computed for this month (if any)
        const billsSnap = await getDocs(
          query(collection(db, "waterBills"), where("month", "==", month))
        );
        const bills = {};
        let anyLocked = false;
        let anyConfirmed = false;
        billsSnap.docs.forEach(d => {
          const data = d.data();
          bills[data.flatId] = data;
          if (data.lockedAt) anyLocked = true;
          if (data.confirmedAt) anyConfirmed = true;
        });
        setWaterBills(bills);
        setBillsLocked(anyLocked);
        setBillsConfirmed(anyConfirmed);

        // Prev month water bills — for carry-forward (unpaid balance)
        const prevBillsSnap = await getDocs(
          query(collection(db, "waterBills"), where("month", "==", prevMonth))
        );
        const cf = {};
        prevBillsSnap.docs.forEach(d => {
          const data = d.data();
          cf[data.flatId] = Number(data.balance || 0);
        });
        setCarryForward(cf);

        setReadings(curr);
        setPrevReadings(prev);
        setSavedFlats(new Set(Object.keys(curr)));

        // Corpus balance: waterDeposit - this month's tanker cost + this month's water payments received
        if (settData) {
          const tankerCostThisMonth = tankerDocs.reduce((s, t) => s + ((t.count || 0) * (t.rate || 0)), 0);

          let waterPaymentsThisMonth = 0;
          try {
            const paySnap = await getDocs(
              query(collection(db, "payments"),
                where("month", "==", month),
                where("type", "in", ["water", "both"]),
                where("status", "==", "confirmed"))
            );
            paySnap.docs.forEach(d => {
              waterPaymentsThisMonth += Number(d.data().amount || 0);
            });
          } catch (e) {
            // payments collection may not support this query shape yet — ignore
          }

          const base = Number(settData.waterDeposit || 0);
          setCorpus(base - tankerCostThisMonth + waterPaymentsThisMonth);
        }
      } catch (e) {
        setError("Failed to load meter readings.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [month, prevMonth]);

  // ── Tanker summary widget: fetch tanker data for every month of the
  // financial year (Apr → current), so Cumulative/This month/Total can be
  // calculated for whichever month tab is selected. Kept separate from the
  // load() above, which only needs the single current month for billing. ──
  useEffect(() => {
    async function loadMonthlyTankerStats() {
      const entries = await Promise.all(
        fyMonths.map(async (m) => {
          const snap = await getDocs(
            query(collection(db, "tankerOrders"), where("month", "==", m))
          );
          const docs = snap.docs.map(d => d.data());
          const count = docs.reduce((s, t) => s + (t.count || 0), 0);
          const cost  = docs.reduce((s, t) => s + ((t.count || 0) * (t.rate || 0)), 0);
          return [m, { count, cost }];
        })
      );
      setTankerMonthlyStats(Object.fromEntries(entries));
    }
    loadMonthlyTankerStats();
  }, [fyMonths]);

  // Cumulative (months before summaryMonth) / This month (summaryMonth) / Total (up to and including summaryMonth)
  const summaryMonthIdx = fyMonths.indexOf(summaryMonth);
  const priorMonths     = summaryMonthIdx > 0 ? fyMonths.slice(0, summaryMonthIdx) : [];
  const upToMonths      = summaryMonthIdx >= 0 ? fyMonths.slice(0, summaryMonthIdx + 1) : [];

  function sumStats(monthKeys) {
    return monthKeys.reduce((acc, m) => {
      const s = tankerMonthlyStats[m] || { count: 0, cost: 0 };
      return { count: acc.count + s.count, cost: acc.cost + s.cost };
    }, { count: 0, cost: 0 });
  }

  const cumulativeStats = sumStats(priorMonths);
  const thisMonthStats  = tankerMonthlyStats[summaryMonth] || { count: 0, cost: 0 };
  const totalStats      = sumStats(upToMonths);

  // ── Watchman wizard: jump to first unsaved flat once data has loaded ──
  useEffect(() => {
    if (isWatchman && !loading) {
      const firstPending = FLAT_ORDER.findIndex(f => !savedFlats.has(f));
      if (firstPending === -1) {
        setWizardDone(true);
        setWizardStep(FLAT_ORDER.length - 1);
      } else {
        setWizardStep(firstPending);
        setWizardDone(false);
      }
      setWizardInput("");
      setWizardStage("entry");
      setWizardError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  function wizardPressDigit(d) {
    setWizardError("");
    setWizardInput(prev => (prev.length < 8 ? prev + d : prev));
  }
  function wizardPressBackspace() {
    setWizardError("");
    setWizardInput(prev => prev.slice(0, -1));
  }
  function wizardPressClear() {
    setWizardError("");
    setWizardInput("");
  }

  function wizardGoToRecheck() {
    if (wizardInput.length !== 8) {
      setWizardError("Please enter all 8 digits.");
      return;
    }
    const flatId = FLAT_ORDER[wizardStep];
    const prev = Number(prevReadings[flatId] || 0);
    const curr = Number(wizardInput);
    if (curr < prev) {
      setWizardError(`Reading can't be less than previous reading (${String(prev).padStart(8, "0")}). Please recheck.`);
      return;
    }
    setWizardStage("recheck");
  }

  function wizardEditEntry() {
    setWizardStage("entry");
  }

  async function wizardConfirmSave() {
    const flatId = FLAT_ORDER[wizardStep];
    setWizardSaving(true);
    setError("");
    try {
      const curr = Number(wizardInput);
      const prev = Number(prevReadings[flatId] || 0);
      const litersUsed = calcLitersUsed(prev, curr);
      await setDoc(doc(db, "meterReadings", `${flatId}_${month}`), {
        flatId,
        month,
        societyId: user?.societyId,
        reading: curr,
        previousReading: prev,
        litersUsed,
        enteredBy: user?.name || "Unknown",
        enteredAt: new Date().toISOString(),
      });
      setReadings(prevR => ({ ...prevR, [flatId]: String(curr) }));
      setSavedFlats(prevS => new Set([...prevS, flatId]));

      setWizardStage("entry");
      setWizardInput("");
      setWizardError("");
      if (wizardStep + 1 < FLAT_ORDER.length) {
        setWizardStep(wizardStep + 1);
      } else {
        setWizardDone(true);
        // All 12 done — generate/refresh calculated bills for MC review
        await generateCalculatedBills();
      }
    } catch (e) {
      setWizardError(`Failed to save Flat ${flatId}. Try again.`);
    } finally {
      setWizardSaving(false);
    }
  }

  function wizardGoBack() {
    if (wizardStep === 0) return;
    const newStep = wizardStep - 1;
    const flatId = FLAT_ORDER[newStep];
    const existing = readings[flatId];
    setWizardStep(newStep);
    setWizardStage("entry");
    setWizardError("");
    setWizardInput(existing !== "" && existing !== undefined ? String(existing).padStart(8, "0") : "");
    setWizardDone(false);
  }

  function wizardGoToFlat(idx) {
    const flatId = FLAT_ORDER[idx];
    const existing = readings[flatId];
    setWizardStep(idx);
    setWizardStage("entry");
    setWizardError("");
    setWizardInput(existing !== "" && existing !== undefined ? String(existing).padStart(8, "0") : "");
    setWizardDone(false);
  }

  function handleChange(flatId, value) {
    setError("");
    setReadings(prev => ({ ...prev, [flatId]: value }));
    // Remove from saved set if edited
    setSavedFlats(prev => { const s = new Set(prev); s.delete(flatId); return s; });
  }

  async function saveSingle(flatId) {
    const reading = readings[flatId];
    if (reading === "" || reading === undefined) {
      setError(`Enter a reading for Flat ${flatId} before saving.`);
      return;
    }
    setSaving(flatId);
    setError("");
    try {
      const prev = Number(prevReadings[flatId] || 0);
      const curr = Number(reading);
      const litersUsed = calcLitersUsed(prev, curr);
      await setDoc(doc(db, "meterReadings", `${flatId}_${month}`), {
        flatId,
        month,
        societyId: user?.societyId,
        reading: curr,
        previousReading: prev,
        litersUsed,
        enteredBy: user?.name || "Unknown",
        enteredAt: new Date().toISOString(),
      });
      setSavedFlats(prev => new Set([...prev, flatId]));
    } catch (e) {
      setError(`Failed to save Flat ${flatId}. Try again.`);
    } finally {
      setSaving(null);
    }
  }

  async function saveAllReadings() {
    const pending = FLAT_ORDER.filter(f => readings[f] !== "" && readings[f] !== undefined && !savedFlats.has(f));
    if (pending.length === 0) { setError("No new readings to save."); return; }
    setSaveAll(true);
    setError("");
    for (const flatId of pending) {
      await saveSingle(flatId);
    }
    setSaveAll(false);

    // If this completed all 12, auto-generate calculated bills (unlocked) for MC review
    const nowSaved = new Set([...savedFlats, ...pending]);
    if (nowSaved.size === 12) {
      await generateCalculatedBills();
    }
  }

  // Stats
  const tankerCost = tankers.reduce((s, t) => s + ((t.count || 0) * (t.rate || 0)), 0);
  const tankerCount = tankers.reduce((s, t) => s + (t.count || 0), 0);
  const totalLiters = FLAT_ORDER.reduce((s, f) => {
    const curr = Number(readings[f] || 0);
    const prev = Number(prevReadings[f] || 0);
    return s + calcLitersUsed(prev, curr);
  }, 0);
  const savedCount = savedFlats.size;
  const allEntered = savedCount === FLAT_ORDER.length;

  // Per-flat liters and calculated bill (live calc, used for review table)
  function litersFor(flatId) {
    const curr = Number(readings[flatId] || 0);
    const prev = Number(prevReadings[flatId] || 0);
    return calcLitersUsed(prev, curr);
  }

  function calculatedBillFor(flatId) {
    if (totalLiters <= 0 || tankerCost <= 0) return 0;
    return Math.round((litersFor(flatId) / totalLiters) * tankerCost);
  }

  // Generate (or refresh) unlocked waterBills docs once all 12 readings are saved
  // FIX — WaterScreen 3.2, frozen 2026-07-13: all flats now save together in
  // ONE Firestore batch instead of one-by-one in a loop, so a network hiccup
  // partway through can never leave some flats with new data and others old.
  async function generateCalculatedBills() {
    setError("");
    try {
      const newBills = {};
      const batch = writeBatch(db);
      for (const flatId of FLAT_ORDER) {
        const liters = litersFor(flatId);
        const billAmount = calculatedBillFor(flatId);
        const existing = waterBills[flatId];
        // Don't overwrite an already-locked bill
        if (existing?.lockedAt) { newBills[flatId] = existing; continue; }

        const docData = {
          flatId,
          month,
          societyId: user?.societyId,
          litersUsed: liters,
          totalLiters,
          tankerCost,
          billAmount,
          carryForward: carryForward[flatId] || 0,
          totalDue: billAmount + (carryForward[flatId] || 0),
          paid: existing?.paid || false,
          paidDate: existing?.paidDate || null,
          balance: existing?.balance ?? (billAmount + (carryForward[flatId] || 0)),
          lockedBy: null,
          lockedAt: null,
        };
        batch.set(doc(db, "waterBills", `${flatId}_${month}`), docData, { merge: true });
        newBills[flatId] = docData;
      }
      await batch.commit();
      setWaterBills(newBills);
    } catch (e) {
      setError("Failed to generate water bills. Try again.");
    }
  }

  function handleEditBill(flatId, value) {
    setEditedBills(prev => ({ ...prev, [flatId]: value }));
  }

  // Step 1 (two-step flow): Secretary / Treasurer confirms readings are correct
  async function confirmReadings() {
    setConfirming(true);
    setError("");
    try {
      const confirmedAt = new Date().toISOString();
      const confirmedBy = user?.name || "Unknown";
      const confirmedBills = {};
      const batch = writeBatch(db);

      for (const flatId of FLAT_ORDER) {
        const calc = calculatedBillFor(flatId);
        const edited = editedBills[flatId];
        const finalBill = (edited !== undefined && edited !== "") ? Number(edited) : calc;
        const cf = carryForward[flatId] || 0;
        const totalDue = finalBill + cf;

        const docData = {
          flatId, month,
          societyId: user?.societyId,
          litersUsed: litersFor(flatId),
          totalLiters, tankerCost,
          billAmount: finalBill,
          carryForward: cf,
          totalDue,
          paid: false, paidDate: null,
          balance: totalDue,
          confirmedBy, confirmedAt,
          lockedBy: null, lockedAt: null,
        };
        batch.set(doc(db, "waterBills", `${flatId}_${month}`), docData, { merge: true });
        confirmedBills[flatId] = docData;
      }
      await batch.commit();
      setWaterBills(confirmedBills);
      setBillsConfirmed(true);
      setError("");
      // No alert — status badge and inline message update automatically
    } catch (e) {
      setError("Failed to confirm readings. Try again.");
    } finally {
      setConfirming(false);
    }
  }

  // Step 2 (two-step) or single step: President approves & locks bills
  async function confirmAndLockBills() {
    setConfirming(true);
    setError("");
    try {
      const lockedBills = {};
      const lockedAt = new Date().toISOString();
      const lockedBy = user?.name || "Unknown";
      const batch = writeBatch(db);

      for (const flatId of FLAT_ORDER) {
        const calc = calculatedBillFor(flatId);
        const edited = editedBills[flatId];
        const finalBill = (edited !== undefined && edited !== "") ? Number(edited) : calc;
        const cf = carryForward[flatId] || 0;
        const totalDue = finalBill + cf;

        const docData = {
          flatId, month,
          societyId: user?.societyId,
          litersUsed: litersFor(flatId),
          totalLiters, tankerCost,
          billAmount: finalBill,
          carryForward: cf,
          totalDue,
          paid: false, paidDate: null,
          balance: totalDue,
          lockedBy, lockedAt,
        };
        batch.set(doc(db, "waterBills", `${flatId}_${month}`), docData, { merge: true });
        lockedBills[flatId] = docData;
      }
      await batch.commit();
      setWaterBills(lockedBills);
      setBillsLocked(true);
    } catch (e) {
      setError("Failed to approve and lock bills. Try again.");
    } finally {
      setConfirming(false);
    }
  }

  if (loading) return (
    <div style={{ textAlign: "center", padding: "40px", color: "#555570", fontSize: "13px" }}>
      Loading meter readings…
    </div>
  );

  const hasGeneratedBills = Object.keys(waterBills).length > 0;

  return (
    <div>

      {/* Header */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>
          {isWatchman ? `🧮 Meter Reading Entry — ${fullMonthYearLabel(month)}` : `💧 Water details for ${currentFYLabel()} (Apr to current month)`}
        </div>
        <div style={{ fontSize: "12px", color: "#555570", marginTop: "3px" }}>
          {isWatchman
            ? "Enter the 8-digit reading for each flat, one at a time."
            : !canEdit
              ? "Meter readings for this month — read only."
              : null}
        </div>
        {isAdyantamUser && month !== todayMonth && (
          <div style={{
            fontSize: "11px", color: "#ffa726", marginTop: "8px",
            background: "#ffa72618", border: "1px solid #ffa72640",
            borderRadius: "8px", padding: "6px 10px", display: "inline-block",
          }}>
            🛠️ Catch-up mode — editing {fullMonthYearLabel(month)}, not today's real month.
            {billsLocked ? " This month is locked and cannot be changed." : ""}
          </div>
        )}
      </div>

      {/* Summary strip */}
      {!isWatchman && (
      <>
      {/* Corpus fund — standalone info card, not tied to any month or pill */}
      {corpus !== null && (
        <div style={{
          background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
          padding: "12px 14px", marginBottom: "20px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontSize: "11px", color: "#8888a8", textTransform: "uppercase" }}>Corpus fund (revolving balance)</div>
            <div style={{ fontSize: "10px", color: "#555570", marginTop: "2px" }}>
              Running balance that keeps water purchases and payments flowing smoothly
            </div>
          </div>
          <div style={{ fontSize: "20px", fontWeight: "700", color: corpus >= 0 ? "#4caf50" : "#ff5252" }}>
            {fmt(corpus)}
          </div>
        </div>
      )}

      {/* Tankers purchase trend — Apr to current */}
      <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0", marginBottom: "10px" }}>
        🚚 Tankers purchase trend — Apr to current
      </div>

      {/* Month tabs — choose which month's tanker summary to view */}
      <div style={{ fontSize: "10px", color: "#8888a8", textTransform: "uppercase", marginBottom: "6px" }}>Month</div>
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
        {fyMonths.map(m => {
          const isSelected = m === summaryMonth;
          return (
            <button
              key={m}
              onClick={() => setSummaryMonth(m)}
              style={{
                flexShrink: 0, padding: "8px 14px", borderRadius: "20px",
                border: `1px solid ${isSelected ? "#6c63ff" : "#2e2e40"}`,
                background: isSelected ? "#6c63ff20" : "#1a1a24",
                color: isSelected ? "#6c63ff" : "#8888a8",
                fontSize: "12px", fontWeight: isSelected ? "700" : "500",
                cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              {monthPillLabel(m)}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        <div style={{
          flex: 1, background: "#1a1a24", border: "1px solid #2e2e40",
          borderRadius: "12px", padding: "10px", textAlign: "center",
        }}>
          <div style={{ fontSize: "12px", color: "#e8e8f0" }}>Cumulative</div>
          <div style={{ fontSize: "10px", color: "#8888a8", marginBottom: "8px" }}>
            {priorMonths.length ? `(Apr to ${monthPillLabel(priorMonths[priorMonths.length - 1])})` : "(none yet)"}
          </div>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>
            {cumulativeStats.count} tankers
          </div>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#42a5f5" }}>
            {fmt(cumulativeStats.cost)}
          </div>
        </div>
        <div style={{
          flex: 1, background: "#1a1a24", border: "1px solid #2e2e40",
          borderRadius: "12px", padding: "10px", textAlign: "center",
        }}>
          <div style={{ fontSize: "12px", color: "#e8e8f0" }}>This month</div>
          <div style={{ fontSize: "10px", color: "#8888a8", marginBottom: "8px" }}>
            ({monthPillLabel(summaryMonth)})
          </div>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>
            {thisMonthStats.count} tankers
          </div>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#42a5f5" }}>
            {fmt(thisMonthStats.cost)}
          </div>
        </div>
        <div style={{
          flex: 1, background: "#1a1a24", border: "1px solid #2e2e40",
          borderRadius: "12px", padding: "10px", textAlign: "center",
        }}>
          <div style={{ fontSize: "12px", color: "#e8e8f0" }}>Total</div>
          <div style={{ fontSize: "10px", color: "#8888a8", marginBottom: "8px" }}>(YTD)</div>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>
            {totalStats.count} tankers
          </div>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#4caf50" }}>
            {fmt(totalStats.cost)}
          </div>
        </div>
      </div>

      </>
      )}


      {/* ── Reading History — visible to every flat, own data only ── */}
      <WaterHistorySection flatId={user?.flatId} />

      {/* ── Usage Trend graph — visible to every flat, own data only ── */}
      <WaterUsageTrendChart flatId={user?.flatId} />

      {/* ── Flat meter readings — current month ── */}
      <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0", marginTop: "24px", marginBottom: "4px" }}>
        🧾 Flat meter readings — current month
      </div>
      {canEdit && (
        <div style={{ fontSize: "12px", color: "#555570", marginBottom: "16px" }}>
          Enter current meter readings for all flats.
        </div>
      )}
      {/* Access note for non-editors */}
      {!canEdit && (
        <div style={{
          background: "#ffa72610", border: "1px solid #ffa72630",
          borderRadius: "10px", padding: "10px 14px",
          fontSize: "12px", color: "#ffa726", marginBottom: "16px",
        }}>
          🔒 Only Watchman and MC members can enter meter readings.
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          background: "#ff525215", border: "1px solid #ff525240",
          borderRadius: "10px", padding: "10px 14px",
          fontSize: "13px", color: "#ff5252", marginBottom: "12px",
        }}>
          ⚠ {error}
        </div>
      )}

      {/* ── Meter entry window gate ─────────────────────────────── */}
      {canEdit && !entryWindowActive && (
        <div style={{
          background: "#ff525210", border: "1px solid #ff525230",
          borderRadius: "10px", padding: "14px 16px", marginBottom: "16px",
        }}>
          <div style={{ fontSize: "13px", color: "#ff5252", fontWeight: "600", marginBottom: "6px" }}>
            🔒 {billsLocked ? "This month's water bill is approved and locked" : "Meter entry is closed"}
          </div>
          <div style={{ fontSize: "12px", color: "#888899" }}>
            {billsLocked
              ? `The President has approved and locked ${fullMonthYearLabel(month)}'s water bill. Meter reading entry will reopen automatically on the 1st of next month.`
              : `Meter reading entry is only open on the 1st of each month. It will reopen on 1 ${nextMonthLabel()}.`}
          </div>
        </div>
      )}

      {/* Readings list — MC table view */}
      {canEdit && !isWatchman && entryWindowActive && (
        <>
          <div style={{ fontSize: "11px", color: "#555570", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600" }}>
            {fullMonthYearLabel(month)}
          </div>

          {FLAT_ORDER.map(flatId => (
            <div key={flatId}>
              <ReadingRow
                flat={flatId}
                reading={readings[flatId] ?? ""}
                prevReading={prevReadings[flatId] ?? ""}
                onChange={handleChange}
                canEdit={canEdit}
                saved={savedFlats.has(flatId)}
              />
              {/* Save per flat button */}
              {canEdit && !savedFlats.has(flatId) && readings[flatId] !== "" && readings[flatId] !== undefined && (
                <div style={{ textAlign: "right", marginTop: "-4px", marginBottom: "8px" }}>
                  <button
                    onClick={() => saveSingle(flatId)}
                    disabled={saving === flatId}
                    style={{
                      background: "#6c63ff", color: "#fff", border: "none",
                      borderRadius: "8px", padding: "6px 14px",
                      fontSize: "12px", fontWeight: "600", cursor: "pointer",
                      opacity: saving === flatId ? 0.7 : 1,
                    }}
                  >
                    {saving === flatId ? "Saving…" : "Save"}
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Save All button */}
          <button
            onClick={saveAllReadings}
            disabled={saveAll}
            style={{
              width: "100%", background: "#6c63ff", color: "#fff",
              border: "none", borderRadius: "10px", padding: "13px",
              fontSize: "15px", fontWeight: "600", marginTop: "8px",
              cursor: saveAll ? "not-allowed" : "pointer",
              opacity: saveAll ? 0.7 : 1,
            }}
          >
            {saveAll ? "Saving All…" : "Save All Pending"}
          </button>
        </>
      )}

      {/* ── Watchman wizard — one flat at a time, numeric keypad ── */}
      {canEdit && isWatchman && entryWindowActive && (
        <div>
          {/* Progress dots */}
          <div style={{ display: "flex", justifyContent: "center", gap: "6px", marginBottom: "16px", flexWrap: "wrap" }}>
            {FLAT_ORDER.map((f, i) => (
              <div
                key={f}
                onClick={() => wizardGoToFlat(i)}
                title={f}
                style={{
                  width: "10px", height: "10px", borderRadius: "50%", cursor: "pointer",
                  background: savedFlats.has(f) ? "#4caf50" : (i === wizardStep && !wizardDone ? "#6c63ff" : "#2e2e40"),
                  border: i === wizardStep && !wizardDone ? "2px solid #6c63ff" : "none",
                }}
              />
            ))}
          </div>

          {wizardDone ? (
            <div style={{
              background: "#4caf5010", border: "1px solid #4caf5040",
              borderRadius: "12px", padding: "24px", textAlign: "center",
            }}>
              <div style={{ fontSize: "32px", marginBottom: "8px" }}>✅</div>
              <div style={{ fontSize: "15px", fontWeight: "700", color: "#4caf50", marginBottom: "6px" }}>
                All 12 meter readings entered!
              </div>
              <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "16px" }}>
                Thank you. The MC will review and confirm the bills for {month}.
              </div>
              <button
                onClick={() => wizardGoToFlat(0)}
                style={{
                  background: "#2e2e40", color: "#e8e8f0", border: "1px solid #3e3e54",
                  borderRadius: "10px", padding: "10px 18px", fontSize: "13px",
                  fontWeight: "600", cursor: "pointer",
                }}
              >
                Review / Edit Readings
              </button>
            </div>
          ) : (
            <>
              {/* Step indicator + flat label */}
              <div style={{ textAlign: "center", marginBottom: "14px" }}>
                <div style={{ fontSize: "11px", color: "#8888a8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                  Step {wizardStep + 1} / {FLAT_ORDER.length}
                </div>
                <div style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: "64px", height: "64px", borderRadius: "16px",
                  background: "#6c63ff18", border: "1px solid #6c63ff40",
                  fontSize: "20px", fontWeight: "700", color: "#6c63ff",
                }}>
                  {FLAT_ORDER[wizardStep]}
                </div>
                {savedFlats.has(FLAT_ORDER[wizardStep]) && (
                  <div style={{ fontSize: "10px", color: "#4caf50", marginTop: "6px" }}>
                    ✅ Already saved — editing will overwrite
                  </div>
                )}
              </div>

              {/* Previous reading reference */}
              <div style={{
                background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
                padding: "10px 14px", marginBottom: "10px", textAlign: "center",
              }}>
                <div style={{ fontSize: "10px", color: "#555570", textTransform: "uppercase", marginBottom: "4px" }}>
                  Previous Reading
                </div>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#8888a8", letterSpacing: "2px", fontFamily: "monospace" }}>
                  {String(Number(prevReadings[FLAT_ORDER[wizardStep]] || 0)).padStart(8, "0")}
                </div>
              </div>

              {wizardStage === "entry" ? (
                <>
                  {/* Current reading display */}
                  <div style={{
                    background: "#0f0f13", border: "1px solid #6c63ff60", borderRadius: "12px",
                    padding: "14px", marginBottom: "12px", textAlign: "center",
                  }}>
                    <div style={{ fontSize: "10px", color: "#555570", textTransform: "uppercase", marginBottom: "4px" }}>
                      New Reading (enter all 8 digits)
                    </div>
                    <div style={{ fontSize: "28px", fontWeight: "700", color: "#e8e8f0", letterSpacing: "4px", fontFamily: "monospace" }}>
                      {wizardInput.padEnd(8, "·")}
                    </div>
                  </div>

                  {wizardError && (
                    <div style={{
                      background: "#ff525215", border: "1px solid #ff525240",
                      borderRadius: "10px", padding: "10px 14px",
                      fontSize: "12px", color: "#ff5252", marginBottom: "12px", textAlign: "center",
                    }}>
                      ⚠ {wizardError}
                    </div>
                  )}

                  {/* Numeric keypad */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginBottom: "12px" }}>
                    {["1","2","3","4","5","6","7","8","9"].map(d => (
                      <button
                        key={d}
                        onClick={() => wizardPressDigit(d)}
                        style={{
                          padding: "16px", fontSize: "20px", fontWeight: "700",
                          background: "#1a1a24", color: "#e8e8f0", border: "1px solid #2e2e40",
                          borderRadius: "10px", cursor: "pointer",
                        }}
                      >
                        {d}
                      </button>
                    ))}
                    <button
                      onClick={wizardPressClear}
                      style={{
                        padding: "16px", fontSize: "13px", fontWeight: "700",
                        background: "#1a1a24", color: "#ff5252", border: "1px solid #2e2e40",
                        borderRadius: "10px", cursor: "pointer",
                      }}
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => wizardPressDigit("0")}
                      style={{
                        padding: "16px", fontSize: "20px", fontWeight: "700",
                        background: "#1a1a24", color: "#e8e8f0", border: "1px solid #2e2e40",
                        borderRadius: "10px", cursor: "pointer",
                      }}
                    >
                      0
                    </button>
                    <button
                      onClick={wizardPressBackspace}
                      style={{
                        padding: "16px", fontSize: "18px", fontWeight: "700",
                        background: "#1a1a24", color: "#ffa726", border: "1px solid #2e2e40",
                        borderRadius: "10px", cursor: "pointer",
                      }}
                    >
                      ⌫
                    </button>
                  </div>

                  {/* Back / Next buttons */}
                  <div style={{ display: "flex", gap: "8px" }}>
                    {wizardStep > 0 && (
                      <button
                        onClick={wizardGoBack}
                        style={{
                          flex: 1, background: "#2e2e40", color: "#8888a8", border: "1px solid #3e3e54",
                          borderRadius: "10px", padding: "13px", fontSize: "14px", fontWeight: "600", cursor: "pointer",
                        }}
                      >
                        ← Back
                      </button>
                    )}
                    <button
                      onClick={wizardGoToRecheck}
                      style={{
                        flex: 2, background: "#6c63ff", color: "#fff", border: "none",
                        borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: "700", cursor: "pointer",
                      }}
                    >
                      Next →
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Recheck stage */}
                  <div style={{
                    background: "#42a5f518", border: "1px solid #42a5f540", borderRadius: "12px",
                    padding: "16px", marginBottom: "12px", textAlign: "center",
                  }}>
                    <div style={{ fontSize: "10px", color: "#8888a8", textTransform: "uppercase", marginBottom: "6px" }}>
                      Please recheck the meter and confirm
                    </div>
                    <div style={{ fontSize: "30px", fontWeight: "700", color: "#42a5f5", letterSpacing: "4px", fontFamily: "monospace" }}>
                      {wizardInput}
                    </div>
                    <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "8px" }}>
                      Flat {FLAT_ORDER[wizardStep]} · Used: {Math.max(0, Number(wizardInput) - Number(prevReadings[FLAT_ORDER[wizardStep]] || 0))}L
                    </div>
                  </div>

                  {wizardError && (
                    <div style={{
                      background: "#ff525215", border: "1px solid #ff525240",
                      borderRadius: "10px", padding: "10px 14px",
                      fontSize: "12px", color: "#ff5252", marginBottom: "12px", textAlign: "center",
                    }}>
                      ⚠ {wizardError}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={wizardEditEntry}
                      disabled={wizardSaving}
                      style={{
                        flex: 1, background: "#2e2e40", color: "#8888a8", border: "1px solid #3e3e54",
                        borderRadius: "10px", padding: "13px", fontSize: "14px", fontWeight: "600",
                        cursor: wizardSaving ? "not-allowed" : "pointer",
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={wizardConfirmSave}
                      disabled={wizardSaving}
                      style={{
                        flex: 2, background: wizardSaving ? "#2e2e40" : "#4caf50", color: "#fff", border: "none",
                        borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: "700",
                        cursor: wizardSaving ? "not-allowed" : "pointer", opacity: wizardSaving ? 0.7 : 1,
                      }}
                    >
                      {wizardSaving ? "Saving…" : "✅ Confirm & Save"}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Bill Review / Confirm / Lock — MC only, once all 12 readings saved ── */}
      {allEntered && canManageBills && (
        <div style={{ marginTop: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>
              💧 Water Bills — {fullMonthYearLabel(month)}
            </div>
            <div style={{
              fontSize: "10px", fontWeight: "600", padding: "3px 9px", borderRadius: "20px",
              background: billsLocked ? "#4caf5018" : billsConfirmed ? "#42a5f518" : "#ffa72618",
              border: `1px solid ${billsLocked ? "#4caf5040" : billsConfirmed ? "#42a5f540" : "#ffa72640"}`,
              color: billsLocked ? "#4caf50" : billsConfirmed ? "#42a5f5" : "#ffa726",
            }}>
              {billsLocked ? "🔒 Locked" : billsConfirmed ? "✅ Confirmed — Awaiting Secretary" : "⏳ Pending Confirmation"}
            </div>
          </div>

          {!hasGeneratedBills && (
            <button
              onClick={generateCalculatedBills}
              style={{
                width: "100%", background: "#2e2e40", color: "#e8e8f0",
                border: "1px solid #3e3e54", borderRadius: "10px", padding: "11px",
                fontSize: "13px", fontWeight: "600", cursor: "pointer", marginBottom: "10px",
              }}
            >
              Calculate Bills
            </button>
          )}

          {hasGeneratedBills && (
            <>
              <div style={{ fontSize: "11px", color: "#555570", marginBottom: "8px" }}>
                Formula: ROUND((liters used ÷ total liters) × tanker cost)
                {!billsLocked && " — edit any flat's bill below if there's a discrepancy."}
              </div>

              {FLAT_ORDER.map(flatId => (
                <BillReviewRow
                  key={flatId}
                  flat={flatId}
                  litersUsed={litersFor(flatId)}
                  calculatedBill={calculatedBillFor(flatId)}
                  editedBill={
                    billsLocked
                      ? (waterBills[flatId]?.billAmount ?? "")
                      : (editedBills[flatId] ?? "")
                  }
                  onChange={handleEditBill}
                  locked={billsLocked}
                />
              ))}

              {/* ── Action buttons based on approval flow ── */}
              {!billsLocked && approvalFlow === "auto" && (
                <div style={{ fontSize: "11px", color: "#4caf50", textAlign: "center", marginTop: "10px",
                  background: "#4caf5010", borderRadius: "8px", padding: "8px" }}>
                  ✅ Auto flow — bills are visible to owners immediately.
                </div>
              )}

              {!billsLocked && approvalFlow === "single" && isPresidentUser && (
                <button onClick={confirmAndLockBills} disabled={confirming} style={{
                  width: "100%", background: confirming ? "#2e2e40" : "#4caf50",
                  color: "#fff", border: "none", borderRadius: "10px", padding: "13px",
                  fontSize: "15px", fontWeight: "700", marginTop: "10px",
                  cursor: confirming ? "not-allowed" : "pointer", opacity: confirming ? 0.7 : 1,
                }}>
                  {confirming ? "Approving…" : "🔒 Approve & Lock Bills"}
                </button>
              )}

              {!billsLocked && approvalFlow === "single" && !isPresidentUser && (
                <div style={{ fontSize: "11px", color: "#8888a8", textAlign: "center", marginTop: "10px",
                  background: "#1a1a24", borderRadius: "8px", padding: "8px", border: "1px solid #2e2e40" }}>
                  ⏳ Waiting for President to approve & lock bills.
                </div>
              )}

              {!billsLocked && approvalFlow === "two-step" && !billsConfirmed && isSecTreas && (
                <button onClick={confirmReadings} disabled={confirming} style={{
                  width: "100%", background: confirming ? "#2e2e40" : "#42a5f5",
                  color: "#fff", border: "none", borderRadius: "10px", padding: "13px",
                  fontSize: "15px", fontWeight: "700", marginTop: "10px",
                  cursor: confirming ? "not-allowed" : "pointer", opacity: confirming ? 0.7 : 1,
                }}>
                  {confirming ? "Confirming…" : "✅ Confirm Readings"}
                </button>
              )}

              {!billsLocked && approvalFlow === "two-step" && !billsConfirmed && isPresidentUser && (
                <div style={{ fontSize: "11px", color: "#8888a8", textAlign: "center", marginTop: "10px",
                  background: "#1a1a24", borderRadius: "8px", padding: "8px", border: "1px solid #2e2e40" }}>
                  ⏳ Waiting for Secretary or Treasurer to confirm readings first.
                </div>
              )}

              {!billsLocked && approvalFlow === "two-step" && !billsConfirmed && !isSecTreas && !isPresidentUser && (
                <div style={{ fontSize: "11px", color: "#8888a8", textAlign: "center", marginTop: "10px",
                  background: "#1a1a24", borderRadius: "8px", padding: "8px", border: "1px solid #2e2e40" }}>
                  ⏳ Awaiting Secretary / Treasurer confirmation, then Secretary to lock.
                </div>
              )}

              {!billsLocked && approvalFlow === "two-step" && billsConfirmed && isSecretaryUser && (
                <div>
                  <div style={{ fontSize: "12px", color: "#42a5f5", textAlign: "center", marginTop: "10px",
                    background: "#42a5f518", borderRadius: "8px", padding: "10px 12px",
                    border: "1px solid #42a5f540", marginBottom: "8px", lineHeight: "1.5" }}>
                    ✅ Readings confirmed by {waterBills[FLAT_ORDER[0]]?.confirmedBy || "MC"}.<br/>
                    <span style={{ fontSize: "11px", color: "#8888a8" }}>Secretary can now finalise & lock.</span>
                  </div>
                  <button onClick={confirmAndLockBills} disabled={confirming} style={{
                    width: "100%", background: confirming ? "#2e2e40" : "#4caf50",
                    color: "#fff", border: "none", borderRadius: "10px", padding: "13px",
                    fontSize: "15px", fontWeight: "700",
                    cursor: confirming ? "not-allowed" : "pointer", opacity: confirming ? 0.7 : 1,
                  }}>
                    {confirming ? "Approving…" : "🔒 Approve & Lock Bills"}
                  </button>
                </div>
              )}

              {!billsLocked && approvalFlow === "two-step" && billsConfirmed && !isSecretaryUser && (
                <div style={{ fontSize: "12px", color: "#42a5f5", textAlign: "center", marginTop: "10px",
                  background: "#42a5f518", borderRadius: "8px", padding: "10px 12px",
                  border: "1px solid #42a5f540", lineHeight: "1.5" }}>
                  ✅ Readings confirmed by {waterBills[FLAT_ORDER[0]]?.confirmedBy || "MC"}.<br/>
                  <span style={{ fontSize: "11px", color: "#8888a8" }}>Waiting for Secretary to finalise & lock.</span>
                </div>
              )}

              {billsLocked && (
                <div style={{
                  fontSize: "11px", color: "#4caf50", textAlign: "center", marginTop: "10px",
                  background: "#4caf5010", borderRadius: "8px", padding: "8px",
                }}>
                  ✅ Bills locked for {month}. Now visible on each flat owner's dashboard.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Owner's own bill — visible once bills are locked ── */}
      {!canEdit && billsLocked && waterBills[user?.flatId] && (
        <div style={{ marginTop: "24px" }}>
          <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0", marginBottom: "8px" }}>
            💧 Your Water Bill — {fullMonthYearLabel(month)}
          </div>
          <div style={{
            background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
            padding: "14px 16px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ fontSize: "12px", color: "#8888a8" }}>Liters Used</span>
              <span style={{ fontSize: "13px", color: "#42a5f5", fontWeight: "600" }}>
                {waterBills[user.flatId].litersUsed}L
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ fontSize: "12px", color: "#8888a8" }}>Water Bill</span>
              <span style={{ fontSize: "13px", color: "#e8e8f0", fontWeight: "600" }}>
                {fmt(waterBills[user.flatId].billAmount)}
              </span>
            </div>
            {waterBills[user.flatId].carryForward > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontSize: "12px", color: "#8888a8" }}>Brought Forward</span>
                <span style={{ fontSize: "13px", color: "#ffa726", fontWeight: "600" }}>
                  {fmt(waterBills[user.flatId].carryForward)}
                </span>
              </div>
            )}
            <div style={{
              display: "flex", justifyContent: "space-between", marginTop: "10px",
              paddingTop: "10px", borderTop: "1px solid #2e2e40",
            }}>
              <span style={{ fontSize: "13px", color: "#e8e8f0", fontWeight: "700" }}>Total Due</span>
              <span style={{ fontSize: "16px", color: "#6c63ff", fontWeight: "700" }}>
                {fmt(waterBills[user.flatId].totalDue)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* MC-only reminder */}
      {canEdit && !isWatchman && (
        <div style={{ fontSize: "11px", color: "#3a3a50", textAlign: "center", marginTop: "16px" }}>
          Only Watchman and MC can enter readings. MC can modify after entry.
        </div>
      )}
    </div>
  );
}
function fmtMonth(m) {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(mo) - 1]}-${String(y).slice(2)}`;
}
function tsToDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}

function fmtDate(ts) {
  const d = tsToDate(ts);
  if (!d) return "";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
}
// FY runs Apr → Mar. Returns months in FY order up to current month only (no future months).
function getFYMonthOptions() {
  const now = new Date();
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const months = [];
  for (let i = 0; i < 12; i++) {
    const m = (3 + i) % 12; // 3 = April (0-indexed)
    const y = fyStartYear + Math.floor((3 + i) / 12);
    const key = `${y}-${String(m + 1).padStart(2, "0")}`;
    if (key > todayKey) break; // stop at current month
    months.push({ key, label: fmtMonth(key) });
  }
  return months;
}
const S = {
  card: {
    background: "#1a1a24", border: "1px solid #2e2e40",
    borderRadius: "14px", padding: "14px", marginBottom: "10px",
  },
  row: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  sectionTitle: {
    fontSize: "11px", color: "#8888a8", textTransform: "uppercase",
    letterSpacing: "0.5px", fontWeight: "600", marginBottom: "8px", marginTop: "16px",
  },
  input: {
    width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
    borderRadius: "10px", padding: "10px 12px", color: "#e8e8f0",
    fontSize: "14px", outline: "none", boxSizing: "border-box", fontFamily: "inherit",
  },
  label: { fontSize: "11px", color: "#8888a8", marginBottom: "5px", display: "block", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600" },
  primaryBtn: {
    width: "100%", background: "#6c63ff", color: "#fff", border: "none",
    borderRadius: "10px", padding: "12px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
  },
  ghostBtn: {
    background: "none", border: "1px solid #2e2e40", color: "#8888a8",
    borderRadius: "8px", padding: "6px 12px", fontSize: "12px", fontWeight: "600", cursor: "pointer",
  },
};

function Badge({ text, color }) {
  const colors = {
    green:  { bg: "#4caf5018", border: "#4caf5040", text: "#4caf50" },
    red:    { bg: "#ff525218", border: "#ff525240", text: "#ff5252" },
    amber:  { bg: "#ffa72618", border: "#ffa72640", text: "#ffa726" },
    blue:   { bg: "#42a5f518", border: "#42a5f540", text: "#42a5f5" },
    purple: { bg: "#6c63ff18", border: "#6c63ff40", text: "#6c63ff" },
  };
  const c = colors[color] || colors.blue;
  return (
    <span style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "600",
    }}>
      {text}
    </span>
  );
}

function MonthChips({ months, active, onSelect, extra }) {
  return (
    <div style={{ display: "flex", gap: "6px", marginBottom: "12px", overflowX: "auto", paddingBottom: "4px" }}>
      {extra}
      {months.map(m => (
        <button key={m.key} onClick={() => onSelect(m.key)} style={{
          flexShrink: 0, padding: "6px 12px", borderRadius: "8px", border: "1px solid",
          borderColor: active === m.key ? "#6c63ff" : "#2e2e40",
          background: active === m.key ? "#6c63ff20" : "transparent",
          color: active === m.key ? "#6c63ff" : "#8888a8",
          fontSize: "12px", fontWeight: "600", cursor: "pointer",
        }}>
          {m.label}
        </button>
      ))}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000000cc", zIndex: 1000,
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }}>
      <div style={{
        background: "#1a1a24", borderRadius: "20px 20px 0 0",
        padding: "20px", width: "100%", maxWidth: "640px",
        maxHeight: "90vh", overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#8888a8", fontSize: "20px", cursor: "pointer" }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
function formatDate(isoDate) {
  if (!isoDate) return "—";
  const [y, m, d] = isoDate.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${d}-${months[Number(m) - 1]}-${y}`;
}
export function TankerOrderModal({ user, settings, onClose, onDone }) {
  const [date, setDate] = useState(todayISO());
  const [count, setCount] = useState("1");
  const [rate, setRate] = useState(settings?.tankerRate ? String(settings.tankerRate) : "");
  const [remarks, setRemarks] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    async function loadSuppliers() {
      try {
        const snap = await getDocs(query(
          collection(db, "contacts"),
          where("category", "==", "Water Tanker Supplier")
        ));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setSuppliers(list);
        if (list.length === 1) setSupplierName(list[0].name);
      } catch (e) {
        console.error("Failed to load suppliers", e);
      }
    }
    loadSuppliers();
  }, []);

  async function handleSubmit() {
    if (!count || Number(count) < 1) { setErr("Number of tankers must be at least 1."); return; }
    if (Number(count) > 5) { setErr("Maximum 5 tankers per order (supplier batch limit)."); return; }
    if (!rate || Number(rate) <= 0) { setErr("Cost per tanker is required."); return; }
    if (!supplierName) { setErr("Please select a supplier."); return; }
    setSaving(true);
    try {
      const numCount = Number(count), numRate = Number(rate);

      // 1. Save the tanker order
      await addDoc(collection(db, "tankerOrders"), {
        date, month: date.slice(0, 7), societyId: user.societyId,
        count: numCount, rate: numRate, amount: numCount * numRate,
        remarks: remarks.trim(), supplierName,
        orderedBy: user.name, orderedAt: serverTimestamp(),
        paymentStatus: "unpaid", expenseId: null,
      });

      // 2. Read current running counter from settings
      const settingsSnap = await getDoc(doc(db, "settings", "init"));
      const currentCount = settingsSnap.data()?.tankerRunningCount || 0;
      const newCount = currentCount + numCount;

      if (newCount >= 5) {
        // 3a. Batch triggered — collect all unpaid orders
        const unpaidSnap = await getDocs(query(
          collection(db, "tankerOrders"),
          where("societyId", "==", user.societyId),
          where("paymentStatus", "==", "unpaid")
        ));
        const unpaidOrders = unpaidSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const batchTotal = unpaidOrders.reduce((s, o) => s + (o.amount || 0), 0);
        const batchTankers = unpaidOrders.reduce((s, o) => s + (o.count || 0), 0);
        const orderIds = unpaidOrders.map(o => o.id);

        // 4. Create batch payment request
        await addDoc(collection(db, "tankerBatchPayments"), {
          orderIds,
          societyId: user.societyId,
          totalTankers: batchTankers,
          totalAmount: batchTotal,
          supplierName,
          status: "pending_treasurer",
          createdAt: serverTimestamp(),
          createdBy: user.name,
          treasurerConfirmedBy: null, treasurerConfirmedAt: null,
          presidentApprovedBy: null, presidentApprovedAt: null,
          paidAt: null,
        });

        // 5. Reset running counter
        await updateDoc(doc(db, "settings", "init"), { tankerRunningCount: 0 });

        // 6. Notify via chat
        await addDoc(collection(db, "chat"), {
          flatId: null, senderName: "System", senderRole: "system", roleIcon: "🚛",
          message: `🚛 Batch Payment Due — ${batchTankers} tankers · ${fmt(batchTotal)} to ${supplierName}. Treasurer please confirm.`,
          timestamp: serverTimestamp(),
        });
      } else {
        // 3b. Not yet 5 — just update the counter
        await updateDoc(doc(db, "settings", "init"), { tankerRunningCount: newCount });
      }

      onDone();
    } catch (e) {
      setErr("Failed to save order. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="New Tanker Order" onClose={onClose}>
      <div style={{ display: "flex", gap: "10px", marginBottom: "14px" }}>
        <div style={{ flex: 1 }}>
          <label style={S.label}>Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={S.input} />
        </div>
        <div style={{ width: "100px" }}>
          <label style={S.label}>No. of Tankers</label>
          <input type="number" min="1" value={count} onChange={e => setCount(e.target.value)} style={S.input} />
        </div>
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={S.label}>Supplier</label>
        <select value={supplierName} onChange={e => setSupplierName(e.target.value)} style={S.input}>
          <option value="">— select supplier —</option>
          {suppliers.map(s => (
            <option key={s.id} value={s.name}>{s.name}{s.phone ? ` · ${s.phone}` : ""}</option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={S.label}>Cost per Tanker (₹)</label>
        <input type="number" min="1" value={rate} onChange={e => setRate(e.target.value)} placeholder="e.g. 1100" style={S.input} />
      </div>

      {count && rate && Number(count) > 0 && Number(rate) > 0 && (
        <div style={{ background: "#6c63ff12", border: "1px solid #6c63ff30", borderRadius: "8px", padding: "10px 14px", marginBottom: "14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "12px", color: "#8888a8" }}>{count} × {fmt(Number(rate))}</span>
          <span style={{ fontSize: "16px", fontWeight: "700", color: "#6c63ff" }}>= {fmt(Number(count) * Number(rate))}</span>
        </div>
      )}

      <div style={{ marginBottom: "16px" }}>
        <label style={S.label}>Remarks (optional)</label>
        <input type="text" value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="e.g. Emergency delivery" style={S.input} />
      </div>

      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}

      <button onClick={handleSubmit} disabled={saving} style={{ ...S.primaryBtn, padding: "14px", fontSize: "15px", opacity: saving ? 0.7 : 1 }}>
        {saving ? "Saving…" : "Log Tanker Order"}
      </button>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════
// EXPENSES — Record Tanker Payment Modal (Treasurer)
// ════════════════════════════════════════════════════════════════
function RecordTankerPaymentModal({ order, user, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function handleSubmit() {
    setSaving(true);
    try {
      const expRef = await addDoc(collection(db, "expenses"), {
        description: `Water tanker — ${order.count} × ${fmt(order.rate)} (${fmtDate(order.date)})`,
        amount: order.amount, date: todayISO(), month: order.month,
        societyId: user.societyId,
        category: "Water Tanker", isRegular: false, isPocket: false,
        approvalStatus: "pending_president", approvedBy: null, approvedAt: null, paidAt: null,
        addedBy: user.name, addedAt: serverTimestamp(), isReimbursed: false,
        tankerOrderId: order.id,
      });
      await updateDoc(doc(db, "tankerOrders", order.id), { expenseId: expRef.id });
      onDone();
    } catch (e) {
      setErr("Failed to record payment. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Record Tanker Payment" onClose={onClose}>
      <div style={S.card}>
        <div style={S.row}>
          <div>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>{order.count} tanker{order.count > 1 ? "s" : ""} @ {fmt(order.rate)}</div>
            <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{fmtDate(order.date)} · ordered by {order.orderedBy}</div>
          </div>
          <div style={{ fontSize: "18px", fontWeight: "700", color: "#42a5f5" }}>{fmt(order.amount)}</div>
        </div>
      </div>
      <div style={{ background: "#42a5f510", border: "1px solid #42a5f530", borderRadius: "10px", padding: "10px 14px", fontSize: "12px", color: "#42a5f5", marginBottom: "16px" }}>
        💡 This creates a non-regular expense linked to this order. President must approve before you can mark it paid.
      </div>
      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}
      <button onClick={handleSubmit} disabled={saving} style={{ ...S.primaryBtn, padding: "14px", fontSize: "15px", opacity: saving ? 0.7 : 1 }}>
        {saving ? "Recording…" : "Record Payment → Send for Approval"}
      </button>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════
// EXPENSES — Batch Payment Action Modal (Treasurer confirm / President approve)
// ════════════════════════════════════════════════════════════════
function BatchPaymentActionModal({ batch, user, isTreasurer, isPresident, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const isTreasurerStep = batch.status === "pending_treasurer";

  async function handleAction() {
    setSaving(true);
    try {
      if (isTreasurerStep && isTreasurer) {
        // Treasurer confirms → move to pending_president
        await updateDoc(doc(db, "tankerBatchPayments", batch.id), {
          status: "pending_president",
          treasurerConfirmedBy: user.name,
          treasurerConfirmedAt: serverTimestamp(),
        });
        await addDoc(collection(db, "chat"), {
          flatId: null, senderName: "System", senderRole: "system", roleIcon: "🚛",
          message: `🚛 Batch Payment Confirmed by Treasurer — ${batch.totalTankers} tankers · ${fmt(batch.totalAmount)}. President please approve.`,
          timestamp: serverTimestamp(),
        });
      } else if (!isTreasurerStep && isPresident) {
        // President approves → mark paid, update all linked tankerOrders
        await updateDoc(doc(db, "tankerBatchPayments", batch.id), {
          status: "paid",
          presidentApprovedBy: user.name,
          presidentApprovedAt: serverTimestamp(),
          paidAt: serverTimestamp(),
        });
        // Mark all linked tankerOrders as paid
        for (const orderId of (batch.orderIds || [])) {
          await updateDoc(doc(db, "tankerOrders", orderId), { paymentStatus: "paid" });
        }
        await addDoc(collection(db, "chat"), {
          flatId: null, senderName: "System", senderRole: "system", roleIcon: "✅",
          message: `✅ Tanker Batch Payment Approved — ${batch.totalTankers} tankers · ${fmt(batch.totalAmount)} paid to ${batch.supplierName}.`,
          timestamp: serverTimestamp(),
        });
      }
      onDone();
    } catch (e) {
      setErr("Failed to process. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={isTreasurerStep ? "Confirm Batch Payment" : "Approve Batch Payment"} onClose={onClose}>
      <div style={S.card}>
        <div style={S.row}>
          <div>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{batch.supplierName}</div>
            <div style={{ fontSize: "12px", color: "#8888a8", marginTop: "2px" }}>{batch.totalTankers} tankers · {batch.orderIds?.length || 0} orders</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "20px", fontWeight: "700", color: "#ffa726" }}>{fmt(batch.totalAmount)}</div>
          </div>
        </div>
      </div>

      <div style={{ background: "#42a5f510", border: "1px solid #42a5f530", borderRadius: "10px", padding: "10px 14px", fontSize: "12px", color: "#42a5f5", marginBottom: "16px" }}>
        {isTreasurerStep
          ? "💡 Confirming will send this batch for President's final approval."
          : "💡 Approving will mark this batch as paid and update all linked tanker orders."}
      </div>

      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}

      <button onClick={handleAction} disabled={saving} style={{ ...S.primaryBtn, padding: "14px", fontSize: "15px", background: saving ? "#2e2e40" : "#4caf50", opacity: saving ? 0.7 : 1 }}>
        {saving ? "Processing…" : isTreasurerStep ? "✅ Confirm — Send to President" : "✅ Approve & Mark Paid"}
      </button>
    </Modal>
  );
}
// ════════════════════════════════════════════════════════════════
// WATER TANKER EXPENSES TAB — moved here from PassbookScreen.jsx
// Renders the "🚛 Water Tanker" toggle view: tanker orders, batch
// payments, and individual payment recording. Has its own month
// filter and its own Firestore fetch, independent of the Maintenance
// expenses list (which stays in PassbookScreen.jsx).
// ════════════════════════════════════════════════════════════════
export function WaterTankerExpensesTab({ user, isMC, isPresident, isTreasurer, settings, refreshKey, setRefreshKey }) {
  const fyMonths = getFYMonthOptions();
  const fyStartMonth = fyMonths[0]?.key || "2026-04";

  const [filterMonth, setFilterMonth] = useState(currentMonthKey());
  const [tankerOrders, setTankerOrders] = useState([]);
  const [allTankerOrders, setAllTankerOrders] = useState([]);
  const [batchPayments, setBatchPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTankerOrder, setShowTankerOrder] = useState(false);
  const [recordingPayment, setRecordingPayment] = useState(null);
  const [actingOnBatch, setActingOnBatch] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);

  // Delete permission — FROZEN 7 July 2026:
  // 1. President by default; President may assign this to other MC members
  //    via settings.tankerDeleteDelegates (array of user names). Watchman
  //    and flat owners can never delete.
  // 2. Delete only works for the actual current real-world calendar month —
  //    never for an older month, even if still unpaid.
  // 3. Delete works freely until a tanker order is swept into a 5-tanker
  //    batch AND the Treasurer confirms that batch. Before the Treasurer
  //    confirms, deleting one order un-forms the whole batch — it goes back
  //    to "still waiting" and will only reach the Treasurer's desk again
  //    once a genuine 5th order comes in. Once the Treasurer has confirmed,
  //    none of that batch's orders can be deleted anymore.
  const hasDeletePermission =
    isPresident ||
    (settings?.tankerDeleteDelegates || []).includes(user?.name);
  const isCurrentCalendarMonth = filterMonth === currentMonthKey();

  function batchForOrder(orderId) {
    return batchPayments.find(b => (b.orderIds || []).includes(orderId));
  }

  function canDeleteOrder(order) {
    if (!hasDeletePermission || !isCurrentCalendarMonth) return false;
    const batch = batchForOrder(order.id);
    if (batch && batch.status !== "pending_treasurer") return false; // locked once Treasurer has confirmed
    return true;
  }

  async function handleDeleteOrder(order) {
    if (!window.confirm("Delete this tanker order? This cannot be undone.")) return;
    const batch = batchForOrder(order.id);
    if (batch && batch.status !== "pending_treasurer") {
      window.alert("This order is already confirmed by the Treasurer and can no longer be deleted.");
      return;
    }
    setDeletingOrderId(order.id);
    try {
      await deleteDoc(doc(db, "tankerOrders", order.id));

      if (batch) {
        // Order was waiting at the Treasurer's desk (not yet confirmed) —
        // un-form the whole batch. The remaining orders go back to being
        // plain unpaid orders, waiting for a genuine 5th to re-trigger.
        await deleteDoc(doc(db, "tankerBatchPayments", batch.id));
        const remainingCount = Math.max((batch.orderIds || []).length - 1, 0);
        await updateDoc(doc(db, "settings", "init"), { tankerRunningCount: remainingCount });
      }

      setRefreshKey(k => k + 1);
    } catch (e) {
      console.error("Failed to delete tanker order", e);
      window.alert("Failed to delete. Please try again.");
    } finally {
      setDeletingOrderId(null);
    }
  }

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const tSnap = await getDocs(query(collection(db, "tankerOrders"), where("month", "==", filterMonth)));
        const orders = tSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        orders.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        setTankerOrders(orders);

        const allTSnap = await getDocs(query(
          collection(db, "tankerOrders"),
          where("month", ">=", fyStartMonth),
          where("month", "<=", filterMonth)
        ));
        setAllTankerOrders(allTSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const batchSnap = await getDocs(query(
          collection(db, "tankerBatchPayments"),
          where("status", "in", ["pending_treasurer", "pending_president"])
        ));
        setBatchPayments(batchSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [filterMonth, refreshKey]);

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 20px", color: "#555570", fontSize: "13px" }}>Loading expenses…</div>;
  }

  const unpaidOrders = tankerOrders.filter(o => o.paymentStatus !== "paid");
  const paidOrders = tankerOrders.filter(o => o.paymentStatus === "paid");
  const cumulativeCount = allTankerOrders.reduce((s, o) => s + (o.count || 0), 0);
  const cumulativeAmount = allTankerOrders.reduce((s, o) => s + (o.amount || 0), 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={S.sectionTitle}>Water Tanker Orders</div>
        {isMC && <button onClick={() => setShowTankerOrder(true)} style={{ ...S.ghostBtn, color: "#6c63ff", borderColor: "#6c63ff40" }}>+ Order</button>}
      </div>
      <MonthChips months={fyMonths} active={filterMonth} onSelect={setFilterMonth} />

      {/* Cumulative Tanker chip — 3-column */}
      <div style={{ background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", padding: "12px 16px", marginBottom: "16px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", textAlign: "center" }}>
          <div style={{ borderRight: "1px solid #2e2e40", paddingRight: "8px" }}>
            <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "2px" }}>Cumulative</div>
            <div style={{ fontSize: "10px", color: "#555570", marginBottom: "6px" }}>
              {allTankerOrders.filter(o => o.month < filterMonth).length > 0
                ? `(${fmtMonth(fyStartMonth)} to ${fyMonths.filter(m => m.key < filterMonth).slice(-1)[0]?.label || fmtMonth(fyStartMonth)})`
                : `(${fmtMonth(fyStartMonth)})`}
            </div>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>
              {allTankerOrders.filter(o => o.month < filterMonth).reduce((s, o) => s + (o.count || 0), 0)} tankers
            </div>
            <div style={{ fontSize: "12px", color: "#8888a8" }}>
              {fmt(allTankerOrders.filter(o => o.month < filterMonth).reduce((s, o) => s + (o.amount || 0), 0))}
            </div>
          </div>
          <div style={{ borderRight: "1px solid #2e2e40", paddingRight: "8px" }}>
            <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "2px" }}>This Month</div>
            <div style={{ fontSize: "10px", color: "#555570", marginBottom: "6px" }}>({fmtMonth(filterMonth)})</div>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>
              {tankerOrders.reduce((s, o) => s + (o.count || 0), 0)} tankers
            </div>
            <div style={{ fontSize: "12px", color: "#8888a8" }}>
              {fmt(tankerOrders.reduce((s, o) => s + (o.amount || 0), 0))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "2px" }}>Total Tankers</div>
            <div style={{ fontSize: "10px", color: "#555570", marginBottom: "6px" }}>&nbsp;</div>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#42a5f5" }}>{cumulativeCount} tankers</div>
            <div style={{ fontSize: "12px", color: "#42a5f5" }}>{fmt(cumulativeAmount)}</div>
          </div>
        </div>
      </div>

      {/* ── Pending Batch Payments ── */}
      {batchPayments.length > 0 && (
        <>
          <div style={S.sectionTitle}>⚠️ Batch Payment Due</div>
          {batchPayments.map(b => (
            <div key={b.id} style={{ ...S.card, border: "1px solid #ffa72650", background: "#ffa72608" }}>
              <div style={S.row}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "700", color: "#ffa726" }}>
                    {b.totalTankers} tankers · {b.supplierName}
                  </div>
                  <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>
                    {b.status === "pending_treasurer" ? "⏳ Waiting for Treasurer to confirm" : "⏳ Waiting for President to approve"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#ffa726" }}>{fmt(b.totalAmount)}</div>
                  <Badge text={b.status === "pending_treasurer" ? "Pending Treasurer" : "Pending President"} color="amber" />
                </div>
              </div>
              {isTreasurer && b.status === "pending_treasurer" && (
                <button onClick={() => setActingOnBatch(b)} style={{ ...S.primaryBtn, marginTop: "10px", padding: "9px", fontSize: "12px" }}>
                  ✅ Confirm Batch Payment
                </button>
              )}
              {isPresident && b.status === "pending_president" && (
                <button onClick={() => setActingOnBatch(b)} style={{ ...S.primaryBtn, marginTop: "10px", padding: "9px", fontSize: "12px", background: "#4caf50" }}>
                  ✅ Approve & Mark Paid
                </button>
              )}
            </div>
          ))}
        </>
      )}

      {unpaidOrders.length > 0 && (
        <>
          <div style={S.sectionTitle}>Unpaid Orders</div>
          {unpaidOrders.map(o => (
            <div key={o.id} style={{ ...S.card, border: "1px solid #ffa72630" }}>
              <div style={S.row}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>{o.count} tanker{o.count > 1 ? "s" : ""} @ {fmt(o.rate)}</div>
                  <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{fmtDate(o.date)} · {o.orderedBy}{o.remarks ? ` · ${o.remarks}` : ""}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#42a5f5" }}>{fmt(o.amount)}</div>
                  <Badge text={o.expenseId ? "Pending Approval" : "Unpaid"} color="amber" />
                </div>
              </div>
              {isTreasurer && !o.expenseId && (
                <button onClick={() => setRecordingPayment(o)} style={{ ...S.primaryBtn, marginTop: "10px", padding: "9px", fontSize: "12px" }}>
                  Record Payment →
                </button>
              )}
              {canDeleteOrder(o) && (
                <button
                  onClick={() => handleDeleteOrder(o)}
                  disabled={deletingOrderId === o.id}
                  style={{
                    ...S.ghostBtn,
                    marginTop: "8px",
                    padding: "8px",
                    fontSize: "12px",
                    color: "#ff5252",
                    borderColor: "#ff525240",
                    opacity: deletingOrderId === o.id ? 0.6 : 1,
                  }}
                >
                  {deletingOrderId === o.id ? "Deleting…" : "🗑 Delete Order"}
                </button>
              )}
            </div>
          ))}
        </>
      )}

      {paidOrders.length > 0 && (
        <>
          <div style={S.sectionTitle}>Paid Orders</div>
          {paidOrders.map(o => (
            <div key={o.id} style={{ ...S.card, padding: "10px 14px" }}>
              <div style={S.row}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>{o.count} tanker{o.count > 1 ? "s" : ""} @ {fmt(o.rate)}</div>
                  <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{fmtDate(o.date)} · {o.orderedBy}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{fmt(o.amount)}</div>
                  <Badge text="Paid" color="green" />
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {tankerOrders.length === 0 && (
        <div style={{ textAlign: "center", padding: "30px", color: "#555570", fontSize: "13px" }}>No tanker orders for this month.</div>
      )}

      {showTankerOrder && (
        <TankerOrderModal user={user} settings={settings} onClose={() => setShowTankerOrder(false)} onDone={() => { setShowTankerOrder(false); setRefreshKey(k => k + 1); }} />
      )}
      {recordingPayment && (
        <RecordTankerPaymentModal order={recordingPayment} user={user} onClose={() => setRecordingPayment(null)} onDone={() => { setRecordingPayment(null); setRefreshKey(k => k + 1); }} />
      )}
      {actingOnBatch && (
        <BatchPaymentActionModal
          batch={actingOnBatch}
          user={user}
          isTreasurer={isTreasurer}
          isPresident={isPresident}
          onClose={() => setActingOnBatch(null)}
          onDone={() => { setActingOnBatch(null); setRefreshKey(k => k + 1); }}
        />
      )}
    </div>
  );
}
// ════════════════════════════════════════════════════════════════
// EQUAL-SPLIT WATER BILLING — new billing method (no meters)
// Formula: Bill per flat = (Total tanker cost ÷ Total flats) + Carry-forward
// Same approval flow (Auto / Single / Two-step) as Meter-based billing.
// No meter reading screen — Watchman/MC never enter readings here.
// ════════════════════════════════════════════════════════════════
function EqualSplitWaterView({ canEdit, user, isMC, isPresident }) {
  const month     = currentMonthKey();
  const prevMonth = prevMonthKey(month);

  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [settings, setSettings] = useState(null);
  const [tankers, setTankers]   = useState([]);
  const [waterBills, setWaterBills] = useState({});
  const [editedBills, setEditedBills] = useState({});
  const [billsConfirmed, setBillsConfirmed] = useState(false);
  const [billsLocked, setBillsLocked]     = useState(false);
  const [confirming, setConfirming]       = useState(false);
  const [carryForward, setCarryForward]   = useState({});
  const [flatOrder, setFlatOrder]         = useState(DEFAULT_FLAT_ORDER); // real per-society flat list, from Firestore

  const canManageBills = canEdit && (isMC ? isMC() : true);
  const approvalFlow   = settings?.waterApprovalFlow || "two-step";
  const isPresidentUser = isPresident ? isPresident() : false;
  const isSecretaryUser = user?.role === "Secretary";
  const isSecTreas      = canManageBills && !isPresidentUser;
  // Local alias so all the FLAT_ORDER usages below automatically use
  // this society's real, live flat list.
  const FLAT_ORDER       = flatOrder;

  useEffect(() => {
    async function load() {
      try {
        const settSnap = await getDoc(doc(db, "settings", "init"));
        if (settSnap.exists()) setSettings(settSnap.data());

        // Flat list + order (from each flat's own sortOrder field)
        setFlatOrder(await loadFlatOrder());

        const tankerSnap = await getDocs(
          query(collection(db, "tankerOrders"), where("month", "==", month))
        );
        setTankers(tankerSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const billsSnap = await getDocs(
          query(collection(db, "waterBills"), where("month", "==", month))
        );
        const bills = {};
        let anyLocked = false;
        let anyConfirmed = false;
        billsSnap.docs.forEach(d => {
          const data = d.data();
          bills[data.flatId] = data;
          if (data.lockedAt) anyLocked = true;
          if (data.confirmedAt) anyConfirmed = true;
        });
        setWaterBills(bills);
        setBillsLocked(anyLocked);
        setBillsConfirmed(anyConfirmed);

        const prevBillsSnap = await getDocs(
          query(collection(db, "waterBills"), where("month", "==", prevMonth))
        );
        const cf = {};
        prevBillsSnap.docs.forEach(d => {
          const data = d.data();
          cf[data.flatId] = Number(data.balance || 0);
        });
        setCarryForward(cf);
      } catch (e) {
        setError("Failed to load water bill data.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [month, prevMonth]);

  const tankerCost  = tankers.reduce((s, t) => s + ((t.count || 0) * (t.rate || 0)), 0);
  const tankerCount = tankers.reduce((s, t) => s + (t.count || 0), 0);
  const shareBase   = tankerCost > 0 ? Math.round(tankerCost / FLAT_ORDER.length) : 0;

  function handleEditBill(flatId, value) {
    setEditedBills(prev => ({ ...prev, [flatId]: value }));
  }

  // FIX — WaterScreen 3.2, frozen 2026-07-13: batched write, same fix as the
  // Meter-based version above.
  async function generateCalculatedBills() {
    setError("");
    try {
      const newBills = {};
      const batch = writeBatch(db);
      for (const flatId of FLAT_ORDER) {
        const existing = waterBills[flatId];
        if (existing?.lockedAt) { newBills[flatId] = existing; continue; }
        const cf = carryForward[flatId] || 0;
        const docData = {
          flatId, month,
          societyId: user?.societyId,
          billingMethod: "equal",
          tankerCost,
          billAmount: shareBase,
          carryForward: cf,
          totalDue: shareBase + cf,
          paid: existing?.paid || false,
          paidDate: existing?.paidDate || null,
          balance: existing?.balance ?? (shareBase + cf),
          lockedBy: null, lockedAt: null,
        };
        batch.set(doc(db, "waterBills", `${flatId}_${month}`), docData, { merge: true });
        newBills[flatId] = docData;
      }
      await batch.commit();
      setWaterBills(newBills);
    } catch (e) {
      setError("Failed to generate water bills. Try again.");
    }
  }

  async function confirmReadings() {
    setConfirming(true);
    setError("");
    try {
      const confirmedAt = new Date().toISOString();
      const confirmedBy = user?.name || "Unknown";
      const confirmedBills = {};
      const batch = writeBatch(db);
      for (const flatId of FLAT_ORDER) {
        const edited = editedBills[flatId];
        const finalBill = (edited !== undefined && edited !== "") ? Number(edited) : shareBase;
        const cf = carryForward[flatId] || 0;
        const totalDue = finalBill + cf;
        const docData = {
          flatId, month, societyId: user?.societyId, billingMethod: "equal", tankerCost,
          billAmount: finalBill, carryForward: cf, totalDue,
          paid: false, paidDate: null, balance: totalDue,
          confirmedBy, confirmedAt, lockedBy: null, lockedAt: null,
        };
        batch.set(doc(db, "waterBills", `${flatId}_${month}`), docData, { merge: true });
        confirmedBills[flatId] = docData;
      }
      await batch.commit();
      setWaterBills(confirmedBills);
      setBillsConfirmed(true);
    } catch (e) {
      setError("Failed to confirm bills. Try again.");
    } finally {
      setConfirming(false);
    }
  }

  async function confirmAndLockBills() {
    setConfirming(true);
    setError("");
    try {
      const lockedBills = {};
      const lockedAt = new Date().toISOString();
      const lockedBy = user?.name || "Unknown";
      const batch = writeBatch(db);
      for (const flatId of FLAT_ORDER) {
        const edited = editedBills[flatId];
        const finalBill = (edited !== undefined && edited !== "") ? Number(edited) : shareBase;
        const cf = carryForward[flatId] || 0;
        const totalDue = finalBill + cf;
        const docData = {
          flatId, month, societyId: user?.societyId, billingMethod: "equal", tankerCost,
          billAmount: finalBill, carryForward: cf, totalDue,
          paid: false, paidDate: null, balance: totalDue,
          lockedBy, lockedAt,
        };
        batch.set(doc(db, "waterBills", `${flatId}_${month}`), docData, { merge: true });
        lockedBills[flatId] = docData;
      }
      await batch.commit();
      setWaterBills(lockedBills);
      setBillsLocked(true);
    } catch (e) {
      setError("Failed to approve and lock bills. Try again.");
    } finally {
      setConfirming(false);
    }
  }

  if (loading) return (
    <div style={{ textAlign: "center", padding: "40px", color: "#555570", fontSize: "13px" }}>
      Loading water bills…
    </div>
  );

  const hasGeneratedBills = Object.keys(waterBills).length > 0;

  return (
    <div>
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>💧 Water — {fullMonthYearLabel(month)}</div>
        <div style={{ fontSize: "12px", color: "#555570", marginTop: "3px" }}>
          Equal-split billing — the tanker cost is divided equally across all flats.
        </div>
      </div>

      {/* Summary strip */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        <div style={{ flex: 1, background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", padding: "10px", textAlign: "center" }}>
          <div style={{ fontSize: "10px", color: "#8888a8", textTransform: "uppercase", marginBottom: "4px" }}>Tanker Cost</div>
          <div style={{ fontSize: "18px", fontWeight: "700", color: "#6c63ff" }}>{tankerCount > 0 ? fmt(tankerCost) : "—"}</div>
        </div>
        <div style={{ flex: 1, background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", padding: "10px", textAlign: "center" }}>
          <div style={{ fontSize: "10px", color: "#8888a8", textTransform: "uppercase", marginBottom: "4px" }}>Flats</div>
          <div style={{ fontSize: "18px", fontWeight: "700", color: "#e8e8f0" }}>{FLAT_ORDER.length}</div>
        </div>
        <div style={{ flex: 1, background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", padding: "10px", textAlign: "center" }}>
          <div style={{ fontSize: "10px", color: "#8888a8", textTransform: "uppercase", marginBottom: "4px" }}>Base Share</div>
          <div style={{ fontSize: "18px", fontWeight: "700", color: "#42a5f5" }}>{shareBase > 0 ? fmt(shareBase) : "—"}</div>
        </div>
      </div>

      {!canEdit && (
        <div style={{ background: "#ffa72610", border: "1px solid #ffa72630", borderRadius: "10px", padding: "10px 14px", fontSize: "12px", color: "#ffa726", marginBottom: "16px" }}>
          🔒 Only MC members can generate and approve water bills.
        </div>
      )}

      {error && (
        <div style={{ background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#ff5252", marginBottom: "12px" }}>
          ⚠ {error}
        </div>
      )}

      {/* ── Bill Review / Confirm / Lock — MC only ── */}
      {canManageBills && (
        <div style={{ marginTop: "8px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>💧 Water Bills — {fullMonthYearLabel(month)}</div>
            <div style={{
              fontSize: "10px", fontWeight: "600", padding: "3px 9px", borderRadius: "20px",
              background: billsLocked ? "#4caf5018" : billsConfirmed ? "#42a5f518" : "#ffa72618",
              border: `1px solid ${billsLocked ? "#4caf5040" : billsConfirmed ? "#42a5f540" : "#ffa72640"}`,
              color: billsLocked ? "#4caf50" : billsConfirmed ? "#42a5f5" : "#ffa726",
            }}>
              {billsLocked ? "🔒 Locked" : billsConfirmed ? "✅ Confirmed — Awaiting Secretary" : "⏳ Pending Confirmation"}
            </div>
          </div>

          {!hasGeneratedBills && (
            <button onClick={generateCalculatedBills} style={{
              width: "100%", background: "#2e2e40", color: "#e8e8f0",
              border: "1px solid #3e3e54", borderRadius: "10px", padding: "11px",
              fontSize: "13px", fontWeight: "600", cursor: "pointer", marginBottom: "10px",
            }}>
              Calculate Bills
            </button>
          )}

          {hasGeneratedBills && (
            <>
              <div style={{ fontSize: "11px", color: "#555570", marginBottom: "8px" }}>
                Formula: (Total tanker cost ÷ Total flats) + Carry-forward
                {!billsLocked && " — edit any flat's bill below if there's a discrepancy."}
              </div>

              {FLAT_ORDER.map(flatId => {
                const editedBill = billsLocked
                  ? (waterBills[flatId]?.billAmount ?? "")
                  : (editedBills[flatId] ?? "");
                const isEdited = editedBill !== "" && Number(editedBill) !== shareBase;
                return (
                  <div key={flatId} style={{
                    display: "flex", alignItems: "center", gap: "10px",
                    background: "#1a1a24", border: `1px solid ${isEdited ? "#ffa72640" : "#2e2e40"}`,
                    borderRadius: "10px", padding: "10px 12px", marginBottom: "6px",
                  }}>
                    <div style={{
                      width: "40px", height: "40px", borderRadius: "8px",
                      background: "#6c63ff18", border: "1px solid #6c63ff40",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "12px", fontWeight: "700", color: "#6c63ff", flexShrink: 0,
                    }}>
                      {flatId}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "10px", color: "#555570", marginBottom: "2px" }}>Equal Share</div>
                      <div style={{ fontSize: "13px", color: "#8888a8" }}>{fmt(shareBase)}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "10px", color: "#555570", marginBottom: "2px" }}>
                        Final Bill {isEdited && <span style={{ color: "#ffa726" }}>(edited)</span>}
                      </div>
                      {billsLocked ? (
                        <div style={{ fontSize: "13px", color: "#e8e8f0", fontWeight: "600" }}>
                          {fmt(editedBill !== "" ? editedBill : shareBase)}
                        </div>
                      ) : (
                        <input
                          type="number"
                          value={editedBill}
                          placeholder={String(shareBase)}
                          onChange={e => handleEditBill(flatId, e.target.value)}
                          style={{
                            width: "100%", background: "#0f0f13",
                            border: `1px solid ${isEdited ? "#ffa72660" : "#2e2e40"}`,
                            borderRadius: "8px", padding: "6px 10px",
                            color: "#e8e8f0", fontSize: "13px",
                            outline: "none", boxSizing: "border-box",
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}

              {/* ── Action buttons based on approval flow (same as Meter-based) ── */}
              {!billsLocked && approvalFlow === "auto" && (
                <div style={{ fontSize: "11px", color: "#4caf50", textAlign: "center", marginTop: "10px",
                  background: "#4caf5010", borderRadius: "8px", padding: "8px" }}>
                  ✅ Auto flow — bills are visible to owners immediately.
                </div>
              )}

              {!billsLocked && approvalFlow === "single" && isPresidentUser && (
                <button onClick={confirmAndLockBills} disabled={confirming} style={{
                  width: "100%", background: confirming ? "#2e2e40" : "#4caf50",
                  color: "#fff", border: "none", borderRadius: "10px", padding: "13px",
                  fontSize: "15px", fontWeight: "700", marginTop: "10px",
                  cursor: confirming ? "not-allowed" : "pointer", opacity: confirming ? 0.7 : 1,
                }}>
                  {confirming ? "Approving…" : "🔒 Approve & Lock Bills"}
                </button>
              )}

              {!billsLocked && approvalFlow === "single" && !isPresidentUser && (
                <div style={{ fontSize: "11px", color: "#8888a8", textAlign: "center", marginTop: "10px",
                  background: "#1a1a24", borderRadius: "8px", padding: "8px", border: "1px solid #2e2e40" }}>
                  ⏳ Waiting for President to approve & lock bills.
                </div>
              )}

              {!billsLocked && approvalFlow === "two-step" && !billsConfirmed && isSecTreas && (
                <button onClick={confirmReadings} disabled={confirming} style={{
                  width: "100%", background: confirming ? "#2e2e40" : "#42a5f5",
                  color: "#fff", border: "none", borderRadius: "10px", padding: "13px",
                  fontSize: "15px", fontWeight: "700", marginTop: "10px",
                  cursor: confirming ? "not-allowed" : "pointer", opacity: confirming ? 0.7 : 1,
                }}>
                  {confirming ? "Confirming…" : "✅ Confirm Bills"}
                </button>
              )}

              {!billsLocked && approvalFlow === "two-step" && !billsConfirmed && isPresidentUser && (
                <div style={{ fontSize: "11px", color: "#8888a8", textAlign: "center", marginTop: "10px",
                  background: "#1a1a24", borderRadius: "8px", padding: "8px", border: "1px solid #2e2e40" }}>
                  ⏳ Waiting for Secretary or Treasurer to confirm bills first.
                </div>
              )}

              {!billsLocked && approvalFlow === "two-step" && !billsConfirmed && !isSecTreas && !isPresidentUser && (
                <div style={{ fontSize: "11px", color: "#8888a8", textAlign: "center", marginTop: "10px",
                  background: "#1a1a24", borderRadius: "8px", padding: "8px", border: "1px solid #2e2e40" }}>
                  ⏳ Awaiting Secretary / Treasurer confirmation, then Secretary to lock.
                </div>
              )}

              {!billsLocked && approvalFlow === "two-step" && billsConfirmed && isSecretaryUser && (
                <div>
                  <div style={{ fontSize: "12px", color: "#42a5f5", textAlign: "center", marginTop: "10px",
                    background: "#42a5f518", borderRadius: "8px", padding: "10px 12px",
                    border: "1px solid #42a5f540", marginBottom: "8px", lineHeight: "1.5" }}>
                    ✅ Confirmed by {waterBills[FLAT_ORDER[0]]?.confirmedBy || "MC"}.<br/>
                    <span style={{ fontSize: "11px", color: "#8888a8" }}>Secretary can now finalise & lock.</span>
                  </div>
                  <button onClick={confirmAndLockBills} disabled={confirming} style={{
                    width: "100%", background: confirming ? "#2e2e40" : "#4caf50",
                    color: "#fff", border: "none", borderRadius: "10px", padding: "13px",
                    fontSize: "15px", fontWeight: "700",
                    cursor: confirming ? "not-allowed" : "pointer", opacity: confirming ? 0.7 : 1,
                  }}>
                    {confirming ? "Approving…" : "🔒 Approve & Lock Bills"}
                  </button>
                </div>
              )}

              {!billsLocked && approvalFlow === "two-step" && billsConfirmed && !isSecretaryUser && (
                <div style={{ fontSize: "12px", color: "#42a5f5", textAlign: "center", marginTop: "10px",
                  background: "#42a5f518", borderRadius: "8px", padding: "10px 12px",
                  border: "1px solid #42a5f540", lineHeight: "1.5" }}>
                  ✅ Confirmed by {waterBills[FLAT_ORDER[0]]?.confirmedBy || "MC"}.<br/>
                  <span style={{ fontSize: "11px", color: "#8888a8" }}>Waiting for Secretary to finalise & lock.</span>
                </div>
              )}

              {billsLocked && (
                <div style={{ fontSize: "11px", color: "#4caf50", textAlign: "center", marginTop: "10px",
                  background: "#4caf5010", borderRadius: "8px", padding: "8px" }}>
                  ✅ Bills locked for {month}. Now visible on each flat owner's dashboard.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Owner's own bill — visible once bills are locked ── */}
      {!canEdit && billsLocked && waterBills[user?.flatId] && (
        <div style={{ marginTop: "24px" }}>
          <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0", marginBottom: "8px" }}>
            💧 Your Water Bill — {fullMonthYearLabel(month)}
          </div>
          <div style={{ background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ fontSize: "12px", color: "#8888a8" }}>Water Bill (Equal Share)</span>
              <span style={{ fontSize: "13px", color: "#e8e8f0", fontWeight: "600" }}>
                {fmt(waterBills[user.flatId].billAmount)}
              </span>
            </div>
            {waterBills[user.flatId].carryForward > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontSize: "12px", color: "#8888a8" }}>Brought Forward</span>
                <span style={{ fontSize: "13px", color: "#ffa726", fontWeight: "600" }}>
                  {fmt(waterBills[user.flatId].carryForward)}
                </span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #2e2e40" }}>
              <span style={{ fontSize: "13px", color: "#e8e8f0", fontWeight: "700" }}>Total Due</span>
              <span style={{ fontSize: "16px", color: "#6c63ff", fontWeight: "700" }}>
                {fmt(waterBills[user.flatId].totalDue)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// NOT OPTED VIEW — shown when a society has chosen "not_needed" in
// the Setup Wizard (society has its own sufficient water supply and
// does not want the Water Management module at all).
// ════════════════════════════════════════════════════════════════
function NotOptedWaterView() {
  return (
    <div style={{ textAlign: "center", padding: "48px 24px" }}>
      <img
        src={waterTankerIcon}
        alt="Water tanker"
        style={{ width: "160px", height: "160px", objectFit: "contain", marginBottom: "20px" }}
      />
      <p style={{ fontSize: "15px", fontWeight: 500, color: "#2C2C2A", margin: 0, lineHeight: 1.5 }}>
        Water Management Module is not selected, since you don't need to buy water
      </p>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// WATER SCREEN — router. Reads the society's billing method once and
// shows the matching view. This is the ONLY export other files should
// use to show the Water tab.
// ════════════════════════════════════════════════════════════════
export default function WaterScreen(props) {
  const [routeReady, setRouteReady] = useState(false);
  const [billingMethod, setBillingMethod] = useState("meter");

  useEffect(() => {
    let cancelled = false;
    async function loadMethod() {
      try {
        const snap = await getDoc(doc(db, "settings", "init"));
        if (!cancelled && snap.exists()) {
          const method = snap.data().waterBillingMethod;
          setBillingMethod(
            method === "not_needed" ? "not_needed" :
            method === "equal" ? "equal" :
            "meter"
          );
        }
      } catch (e) {
        // default stays "meter" — safe fallback, matches BSKR's setup
      } finally {
        if (!cancelled) setRouteReady(true);
      }
    }
    loadMethod();
    return () => { cancelled = true; };
  }, []);

  if (!routeReady) return (
    <div style={{ textAlign: "center", padding: "40px", color: "#555570", fontSize: "13px" }}>
      Loading…
    </div>
  );

  if (billingMethod === "not_needed") return <NotOptedWaterView />;

  return billingMethod === "equal"
    ? <EqualSplitWaterView {...props} />
    : <MeterBasedWaterView {...props} />;
}


